import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ArrowLeft, Upload, Download, RefreshCw, CheckCircle, XCircle, AlertTriangle, Edit, FileText, ToggleLeft, ToggleRight, Trash2, AlertCircle, Copy, Info, CalendarCheck, ShieldCheck, Clock, FileUp } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import useClientFiling from '../hooks/useClientFiling';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GSTIN_LENGTH = 15;
const HTTP_PAYLOAD_TOO_LARGE = 413;

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const CATEGORIES = ['B2B-Local', 'B2B-Interstate', 'B2C-Local', 'B2C-Interstate', 'Exempt', 'Export', 'SEZ Sale', 'Deemed Sales'];

const categoryBadgeClass = {
  'B2B-Local': 'badge-b2b-local', 'B2B-Interstate': 'badge-b2b-interstate',
  'B2C-Local': 'badge-b2c-local', 'B2C-Interstate': 'badge-b2c-interstate',
  'Exempt': 'badge-exempt', 'Export': 'badge-export',
  'SEZ Sale': 'badge-sez', 'Deemed Sales': 'badge-deemed',
};

const formatCurrency = (value) =>
  (value || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 });
const formatNumber = (value) =>
  (value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTH_SHORT = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatPeriodLabel = (period) => {
  if (!period) return '';
  const [y, m] = period.split('-');
  return `${MONTH_SHORT[parseInt(m, 10)] || m} ${y}`;
};

const SUPPLY_TYPE_STYLES = {
  B2B: 'bg-blue-50 text-blue-700',
  B2C: 'bg-green-50 text-green-700',
};
const getSupplyTypeBadgeClass = (type) => SUPPLY_TYPE_STYLES[type] || 'bg-slate-50 text-slate-500';

const TaxableBreakdown = ({ txn }) => {
  const roundOff = (txn.invoice_value || 0) - (txn.taxable_value || 0) - (txn.igst || 0) - (txn.cgst || 0) - (txn.sgst || 0) - (txn.cess || 0);
  const fmt = (v) => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="numeric font-mono cursor-help inline-flex items-center gap-1" data-testid={`taxable-breakdown-${txn.id}`}>
          {formatNumber(txn.taxable_value)}
          <Info className="w-3 h-3 text-slate-400 shrink-0" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="left" className="bg-slate-900 text-slate-100 px-3 py-2.5 rounded text-[11px] font-mono leading-relaxed max-w-[260px]">
        <div className="space-y-0.5">
          <div className="flex justify-between gap-4"><span className="text-slate-400">Invoice Total</span><span>{fmt(txn.invoice_value)}</span></div>
          {(txn.igst > 0) && <div className="flex justify-between gap-4"><span className="text-slate-400">&minus; IGST</span><span>{fmt(txn.igst)}</span></div>}
          {(txn.cgst > 0) && <div className="flex justify-between gap-4"><span className="text-slate-400">&minus; CGST</span><span>{fmt(txn.cgst)}</span></div>}
          {(txn.sgst > 0) && <div className="flex justify-between gap-4"><span className="text-slate-400">&minus; SGST</span><span>{fmt(txn.sgst)}</span></div>}
          {(txn.cess > 0) && <div className="flex justify-between gap-4"><span className="text-slate-400">&minus; Cess</span><span>{fmt(txn.cess)}</span></div>}
          {Math.abs(roundOff) >= 0.01 && <div className="flex justify-between gap-4"><span className="text-slate-400">&minus; Round Off</span><span>{fmt(roundOff)}</span></div>}
          <div className="border-t border-slate-700 pt-1 mt-1 flex justify-between gap-4 font-semibold"><span className="text-emerald-400">= Taxable</span><span className="text-emerald-300">{fmt(txn.taxable_value)}</span></div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

function reconStatusBadge(status) {
  if (status === 'Matched') return 'badge-matched';
  if (status === 'Value Mismatch') return 'badge-variance';
  return 'badge-pending';
}

function reconStatusIcon(status) {
  if (status === 'Matched') return <CheckCircle className="w-3 h-3" />;
  if (status === 'Value Mismatch') return <AlertTriangle className="w-3 h-3" />;
  return <XCircle className="w-3 h-3" />;
}

// ─── Small sub-components ───

const LoadingState = () => (
  <div className="p-8 text-center"><div className="spinner mx-auto mb-2" /><p className="text-slate-500">Loading...</p></div>
);

const EmptyState = ({ message }) => (
  <div className="p-8 text-center text-slate-500">{message}</div>
);

const UploadSection = ({ title, description, accept, onUpload, uploading, status, multiple }) => {
  const fileRef = useRef(null);
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm p-4" data-testid={`upload-${title.split(' ')[0].toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-slate-900">{title}</h4>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="file" ref={fileRef} accept={accept} multiple={!!multiple} className="hidden" onChange={() => onUpload(fileRef)} />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" className="rounded-sm">
            {uploading ? <><span className="spinner mr-2" />Uploading...</> : <><Upload className="w-4 h-4 mr-2" />Upload</>}
          </Button>
        </div>
      </div>
      {status && (
        <div className={`mt-3 p-2 rounded-sm text-sm flex items-center gap-2 ${status.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {status.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}{status.message}
        </div>
      )}
    </div>
  );
};

// ─── Table: Transaction Row ───

const TransactionRow = ({ txn, onEdit }) => {
  let rowClass = '';
  if (txn.fy_duplicate) rowClass = 'bg-red-50/60';
  else if (txn.date_mismatch) rowClass = 'bg-amber-50/60';
  return (
    <tr className={rowClass}>
      <td className="font-mono text-xs">
        {txn.voucher_number}
        {txn.fy_duplicate && <span className="ml-1 inline-flex items-center text-red-600" title={`Duplicate in FY (${txn.fy_duplicate_period || 'same period'})`}><Copy className="w-3 h-3" /></span>}
      </td>
      <td className="font-mono text-xs">
        {txn.voucher_date}
        {txn.date_mismatch && <span className="ml-1 inline-flex items-center text-amber-600" title="Date outside selected period"><AlertCircle className="w-3 h-3" /></span>}
      </td>
      <td className="text-xs">{txn.voucher_type}</td>
      <td className="max-w-[150px] truncate text-xs">{txn.party_name}</td>
      <td className="font-mono text-xs">{txn.party_gstin || '\u2014'}</td>
      <td><span className={`badge ${categoryBadgeClass[txn.category] || 'badge-exempt'}`}>{txn.category}</span></td>
      <td className="numeric font-mono"><TaxableBreakdown txn={txn} /></td>
      <td className="numeric font-mono">{formatNumber(txn.igst)}</td>
      <td className="numeric font-mono">{formatNumber(txn.cgst)}</td>
      <td className="numeric font-mono">{formatNumber(txn.sgst)}</td>
      <td className="numeric font-mono">{formatNumber(txn.invoice_value)}</td>
      <td>
        <button onClick={() => onEdit(txn)} className="p-1 text-slate-400 hover:text-slate-600" data-testid={`edit-cat-${txn.id}`}>
          <Edit className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
};

// ─── Sub-tab: Summary ───

const SalesRegisterRow = ({ label, code, data, bold, borderTop }) => {
  const f = (v) => v ? formatNumber(v) : '\u2014';
  return (
    <tr className={`${bold ? 'bg-slate-100 font-bold' : ''} ${borderTop ? 'border-t-2 border-slate-400' : ''}`}>
      <td className={`${bold ? 'font-bold' : ''} text-sm`}>{label}</td>
      <td className="text-center text-xs text-slate-500 font-mono">{code}</td>
      <td className="numeric font-mono">{f(data?.turnover)}</td>
      <td className="numeric font-mono">{f(data?.igst)}</td>
      <td className="numeric font-mono">{f(data?.cgst)}</td>
      <td className="numeric font-mono">{f(data?.sgst)}</td>
    </tr>
  );
};

function renderDataContent(loading, hasData, emptyMessage, children) {
  if (loading) return <LoadingState />;
  if (!hasData) return <EmptyState message={emptyMessage} />;
  return children;
}

const SummaryTab = ({ loading, gstr1Summary, totals, transactionsCount, salesRegister, onExport }) => (
  <div className="space-y-4">
    {/* Sales Register */}
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Sales Register Summary</h3>
        <ExportMenu reportType="complete" onExport={onExport} testId="export-complete" />
      </div>
      {renderDataContent(loading, !!salesRegister, "No data. Upload Tally XML to get started.",
        salesRegister && (
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="sales-register-table">
            <thead>
              <tr>
                <th className="w-[160px]">Particulars</th>
                <th className="w-[80px] text-center">Ref</th>
                <th className="numeric">Turnover</th>
                <th className="numeric">IGST</th>
                <th className="numeric">CGST</th>
                <th className="numeric">SGST</th>
              </tr>
            </thead>
            <tbody>
              <SalesRegisterRow label="B2B Sales" code="A" data={salesRegister.b2b_sales} />
              <SalesRegisterRow label="CN - B2B" code="B" data={salesRegister.cn_b2b} />
              <SalesRegisterRow label="B2C Sales" code="C" data={salesRegister.b2c_sales} />
              <SalesRegisterRow label="CN - B2C" code="D" data={salesRegister.cn_b2c} />
              <SalesRegisterRow label="Net B2C Sales" code="E=C-D" data={salesRegister.net_b2c} bold />
              <SalesRegisterRow label="Exempt" code="H" data={salesRegister.exempt} />
              <SalesRegisterRow label="SEZ Supplies" code="G" data={salesRegister.sez_supplies} />
              <SalesRegisterRow label="Export" code="F" data={salesRegister.export} />
              <SalesRegisterRow label="Deemed Sales" code="I" data={salesRegister.deemed_sales} />
              <SalesRegisterRow label="Total Sales" code="A-B+E+F+G+H+I" data={salesRegister.total_sales} bold borderTop />
            </tbody>
          </table>
        </div>
        )
      )}
    </div>

    {/* Category-wise Breakdown */}
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
        <h3 className="font-semibold text-slate-800">Category-wise Breakdown</h3>
      </div>
      {renderDataContent(loading, Object.keys(gstr1Summary).length > 0, "No data.",
        <table className="data-table">
          <thead>
            <tr>
              <th>Category</th><th className="numeric">Invoices</th><th className="numeric">Taxable Value</th>
              <th className="numeric">IGST</th><th className="numeric">CGST</th><th className="numeric">SGST</th>
              <th className="numeric">Total Value</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(gstr1Summary).map(([cat, data]) => (
              <tr key={cat}>
                <td><span className={`badge ${categoryBadgeClass[cat] || 'badge-exempt'}`}>{cat}</span></td>
                <td className="numeric font-mono">{data.count}</td>
                <td className="numeric font-mono">{formatNumber(data.taxable_value)}</td>
                <td className="numeric font-mono">{formatNumber(data.igst)}</td>
                <td className="numeric font-mono">{formatNumber(data.cgst)}</td>
                <td className="numeric font-mono">{formatNumber(data.sgst)}</td>
                <td className="numeric font-mono">{formatNumber(data.invoice_value)}</td>
              </tr>
            ))}
            <tr className="bg-slate-50 font-semibold">
              <td>Total</td>
              <td className="numeric font-mono">{transactionsCount}</td>
              <td className="numeric font-mono">{formatNumber(totals.taxable)}</td>
              <td className="numeric font-mono">{formatNumber(totals.igst)}</td>
              <td className="numeric font-mono">{formatNumber(totals.cgst)}</td>
              <td className="numeric font-mono">{formatNumber(totals.sgst)}</td>
              <td className="numeric font-mono">{formatNumber(totals.total)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  </div>
);

// ─── Sub-tab: All Transactions ───

const AllTransactionsTab = ({ loading, transactions, onEdit }) => {
  const [catFilter, setCatFilter] = React.useState('');
  const [showMismatchOnly, setShowMismatchOnly] = React.useState(false);
  let filtered = catFilter ? transactions.filter(t => t.category === catFilter) : transactions;
  if (showMismatchOnly) filtered = filtered.filter(t => t.date_mismatch);
  const uniqueCats = [...new Set(transactions.map(t => t.category))].sort();
  const dateMismatches = transactions.filter(t => t.date_mismatch).length;
  const fyDuplicates = transactions.filter(t => t.fy_duplicate).length;
  const fyConflictPeriods = [...new Set(transactions.filter(t => t.fy_duplicate && t.fy_duplicate_period).map(t => t.fy_duplicate_period))].sort();

  return (
    <div className="space-y-3">
      {(dateMismatches > 0 || fyDuplicates > 0) && (
        <div className="flex flex-wrap gap-3">
          {dateMismatches > 0 && (
            <div
              className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-800 cursor-pointer hover:bg-amber-100 transition-colors"
              data-testid="date-mismatch-warning"
              onClick={() => setShowMismatchOnly(!showMismatchOnly)}
              title="Click to filter"
            >
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <span><strong>{dateMismatches}</strong> invoice(s) have dates outside the selected period</span>
              <span className="ml-1 text-amber-600 font-medium">{showMismatchOnly ? '[ Clear ]' : '[ Show ]'}</span>
            </div>
          )}
          {fyDuplicates > 0 && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-sm px-3 py-2 text-xs text-red-800" data-testid="fy-duplicate-warning">
              <Copy className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span><strong>{fyDuplicates}</strong> duplicate invoice number(s) in the same FY{fyConflictPeriods.length > 0 && ` — conflicts with: ${fyConflictPeriods.join(', ')}`}</span>
            </div>
          )}
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">All Transactions ({filtered.length}{(catFilter || showMismatchOnly) ? ` of ${transactions.length}` : ''})</h3>
          <div className="flex items-center gap-4">
            <select
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[140px]"
              data-testid="category-filter-select"
            >
              <option value="">All Categories</option>
              {uniqueCats.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1"><span className="w-3 h-2 bg-amber-50 border border-amber-200 rounded-sm inline-block"></span>Date mismatch</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 bg-red-50 border border-red-200 rounded-sm inline-block"></span>FY Duplicate</span>
            </div>
          </div>
        </div>
    {renderDataContent(loading, filtered.length > 0, catFilter ? `No ${catFilter} transactions found.` : "No transactions. Upload Tally XML to get started.",
      <div className="overflow-x-auto max-h-[600px]">
        <table className="data-table" data-testid="all-transactions-table">
          <thead>
            <tr>
              <th>Invoice No</th><th>Date</th><th>Type</th><th>Party Name</th><th>GSTIN</th>
              <th>Category</th><th className="numeric">Taxable</th><th className="numeric">IGST</th>
              <th className="numeric">CGST</th><th className="numeric">SGST</th><th className="numeric">Total</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(txn => <TransactionRow key={txn.id} txn={txn} onEdit={onEdit} />)}
          </tbody>
        </table>
      </div>
    )}
  </div>
  </div>
  );
};

// ─── Sub-tab: B2B ───

const B2BTab = ({ loading, b2bData, onEdit }) => {
  const b2bTransactions = b2bData?.transactions || [];
  const rateSummary = b2bData?.rate_summary || [];

  return (
    <div className="space-y-4">
      {/* Rate-wise summary */}
      {rateSummary.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
            <h3 className="font-semibold text-slate-800">Rate-wise Bifurcation</h3>
          </div>
          <table className="data-table" data-testid="b2b-rate-summary">
            <thead>
              <tr>
                <th>GST Rate</th><th className="numeric">Invoices</th><th className="numeric">Taxable Value</th>
                <th className="numeric">IGST</th><th className="numeric">CGST</th><th className="numeric">SGST</th>
                <th className="numeric">Invoice Value</th>
              </tr>
            </thead>
            <tbody>
              {rateSummary.map(r => (
                <tr key={r.rate}>
                  <td className="font-mono font-medium">{r.rate}</td>
                  <td className="numeric font-mono">{r.count}</td>
                  <td className="numeric font-mono">{formatNumber(r.taxable_value)}</td>
                  <td className="numeric font-mono">{formatNumber(r.igst)}</td>
                  <td className="numeric font-mono">{formatNumber(r.cgst)}</td>
                  <td className="numeric font-mono">{formatNumber(r.sgst)}</td>
                  <td className="numeric font-mono">{formatNumber(r.invoice_value)}</td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-semibold">
                <td>Total</td>
                <td className="numeric font-mono">{rateSummary.reduce((s, r) => s + r.count, 0)}</td>
                <td className="numeric font-mono">{formatNumber(rateSummary.reduce((s, r) => s + r.taxable_value, 0))}</td>
                <td className="numeric font-mono">{formatNumber(rateSummary.reduce((s, r) => s + r.igst, 0))}</td>
                <td className="numeric font-mono">{formatNumber(rateSummary.reduce((s, r) => s + r.cgst, 0))}</td>
                <td className="numeric font-mono">{formatNumber(rateSummary.reduce((s, r) => s + r.sgst, 0))}</td>
                <td className="numeric font-mono">{formatNumber(rateSummary.reduce((s, r) => s + r.invoice_value, 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Invoice list */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
          <h3 className="font-semibold text-slate-800">B2B Invoices ({b2bTransactions.length})</h3>
        </div>
        {loading ? <LoadingState /> : (
          <div className="overflow-x-auto max-h-[500px]">
            <table className="data-table" data-testid="b2b-transactions-table">
              <thead>
                <tr>
                  <th>Invoice No</th><th>Date</th><th>Party Name</th><th>GSTIN</th><th>State</th><th>Rate</th><th>Category</th>
                  <th className="numeric">Taxable</th><th className="numeric">IGST</th><th className="numeric">CGST</th>
                  <th className="numeric">SGST</th><th className="numeric">Total</th><th></th>
                </tr>
              </thead>
              <tbody>
                {b2bTransactions.map(txn => (
                  <tr key={txn.id}>
                    <td className="font-mono text-sm">{txn.voucher_number}</td>
                    <td className="font-mono text-sm">{txn.voucher_date}</td>
                    <td className="max-w-[150px] truncate">{txn.party_name}</td>
                    <td className="font-mono text-sm">{txn.party_gstin}</td>
                    <td className="font-mono text-sm">{txn.state_code || (txn.party_gstin ? txn.party_gstin.substring(0, 2) : '')}</td>
                    <td className="font-mono text-sm text-center">{txn.gst_rate}%</td>
                    <td><span className={`badge ${categoryBadgeClass[txn.category]}`}>{txn.category}</span></td>
                    <td className="numeric font-mono"><TaxableBreakdown txn={txn} /></td>
                    <td className="numeric font-mono">{formatNumber(txn.igst)}</td>
                    <td className="numeric font-mono">{formatNumber(txn.cgst)}</td>
                    <td className="numeric font-mono">{formatNumber(txn.sgst)}</td>
                    <td className="numeric font-mono">{formatNumber(txn.invoice_value)}</td>
                    <td>
                      <button onClick={() => onEdit(txn)} className="p-1 text-slate-400 hover:text-slate-600">
                        <Edit className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Sub-tab: B2C State-wise ───

const B2CStatewiseTab = ({ loading, b2cData, onExport }) => {
  const stateSummary = b2cData?.state_summary || b2cData || [];
  const rateStateSummary = b2cData?.rate_state_summary || [];

  return (
    <div className="space-y-4">
      {/* Rate-wise + State-wise combined */}
      {rateStateSummary.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">B2C Rate-wise State-wise Summary</h3>
            <ExportMenu reportType="gstr1-b2c" onExport={onExport} testId="export-b2c" />
          </div>
          <table className="data-table" data-testid="b2c-rate-state-table">
            <thead>
              <tr>
                <th>Rate</th><th>State Code</th><th>State Name</th><th className="numeric">Invoices</th>
                <th className="numeric">Taxable Value</th><th className="numeric">IGST</th>
                <th className="numeric">CGST</th><th className="numeric">SGST</th>
              </tr>
            </thead>
            <tbody>
              {rateStateSummary.map(item => (
                <tr key={`${item.rate}-${item.state_code}`}>
                  <td className="font-mono font-medium">{item.rate}</td>
                  <td className="font-mono">{item.state_code}</td>
                  <td>{item.state_name}</td>
                  <td className="numeric font-mono">{item.count}</td>
                  <td className="numeric font-mono">{formatNumber(item.taxable_value)}</td>
                  <td className="numeric font-mono">{formatNumber(item.igst)}</td>
                  <td className="numeric font-mono">{formatNumber(item.cgst)}</td>
                  <td className="numeric font-mono">{formatNumber(item.sgst)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* State-wise summary */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
          <h3 className="font-semibold text-slate-800">B2C State-wise Summary</h3>
        </div>
        {renderDataContent(loading, (Array.isArray(stateSummary) ? stateSummary : []).length > 0, "No B2C data",
          <table className="data-table" data-testid="b2c-statewise-table">
            <thead>
              <tr>
                <th>State Code</th><th>State Name</th><th className="numeric">Invoices</th>
                <th className="numeric">Taxable Value</th><th className="numeric">IGST</th>
                <th className="numeric">CGST</th><th className="numeric">SGST</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(stateSummary) ? stateSummary : []).map(item => (
                <tr key={item.state_code}>
                  <td className="font-mono">{item.state_code}</td>
                  <td>{item.state_name}</td>
                  <td className="numeric font-mono">{item.count}</td>
                  <td className="numeric font-mono">{formatNumber(item.taxable_value)}</td>
                  <td className="numeric font-mono">{formatNumber(item.igst)}</td>
                  <td className="numeric font-mono">{formatNumber(item.cgst)}</td>
                  <td className="numeric font-mono">{formatNumber(item.sgst)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ─── Sub-tab: HSN ───

const HSNTab = ({ loading, hsnSummary, onExport }) => (
  <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
      <h3 className="font-semibold text-slate-800">HSN Summary</h3>
      <ExportMenu reportType="gstr1-hsn" onExport={onExport} testId="export-hsn" />
    </div>
    {renderDataContent(loading, hsnSummary.length > 0, "No HSN data. Upload Rate Master for HSN mapping.",
      <table className="data-table" data-testid="hsn-summary-table">
        <thead>
          <tr>
            <th>HSN Code</th><th>Description</th><th>Type</th><th>UQC</th><th className="numeric">Rate</th>
            <th className="numeric">Quantity</th><th className="numeric">Taxable Value</th>
            <th className="numeric">IGST</th><th className="numeric">CGST</th><th className="numeric">SGST</th>
            <th className="numeric">Total Tax</th>
          </tr>
        </thead>
        <tbody>
          {hsnSummary.map((item, idx) => (
            <tr key={`${item.hsn_code}-${item.rate}-${item.supply_type}-${idx}`}>
              <td className="font-mono">{item.hsn_code}</td>
              <td className="max-w-[200px] truncate">{item.description}</td>
              <td><span className={`text-xs font-medium px-1.5 py-0.5 rounded-sm ${getSupplyTypeBadgeClass(item.supply_type)}`}>{item.supply_type || 'Other'}</span></td>
              <td>{item.uqc}</td>
              <td className="numeric font-mono">{item.rate}%</td>
              <td className="numeric font-mono">{item.quantity ? formatNumber(item.quantity) : '\u2014'}</td>
              <td className="numeric font-mono">{formatNumber(item.taxable_value)}</td>
              <td className="numeric font-mono">{formatNumber(item.igst)}</td>
              <td className="numeric font-mono">{formatNumber(item.cgst)}</td>
              <td className="numeric font-mono">{formatNumber(item.sgst)}</td>
              <td className="numeric font-mono font-medium">{formatNumber((item.igst || 0) + (item.cgst || 0) + (item.sgst || 0))}</td>
            </tr>
          ))}
          <tr className="bg-slate-50 font-semibold">
            <td colSpan={6}>Total</td>
            <td className="numeric font-mono">{formatNumber(hsnSummary.reduce((s, i) => s + (i.taxable_value || 0), 0))}</td>
            <td className="numeric font-mono">{formatNumber(hsnSummary.reduce((s, i) => s + (i.igst || 0), 0))}</td>
            <td className="numeric font-mono">{formatNumber(hsnSummary.reduce((s, i) => s + (i.cgst || 0), 0))}</td>
            <td className="numeric font-mono">{formatNumber(hsnSummary.reduce((s, i) => s + (i.sgst || 0), 0))}</td>
            <td className="numeric font-mono font-medium">{formatNumber(hsnSummary.reduce((s, i) => s + (i.igst || 0) + (i.cgst || 0) + (i.sgst || 0), 0))}</td>
          </tr>
        </tbody>
      </table>
    )}
  </div>
);

// ─── Sub-tab: E-Invoice Reconciliation ───

const ReconTab = ({ loading, reconciliation, reconStats, onExport }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <div className="bg-white border border-slate-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-slate-500">Total</p><p className="text-xl font-semibold font-mono">{reconStats.total}</p></div>
      <div className="bg-green-50 border border-green-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-green-600">Matched</p><p className="text-xl font-semibold font-mono text-green-700">{reconStats.matched}</p></div>
      <div className="bg-red-50 border border-red-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-red-600">Variance</p><p className="text-xl font-semibold font-mono text-red-700">{reconStats.variance}</p></div>
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-amber-600">Only Books</p><p className="text-xl font-semibold font-mono text-amber-700">{reconStats.onlyBooks}</p></div>
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-blue-600">Only E-Inv</p><p className="text-xl font-semibold font-mono text-blue-700">{reconStats.onlyEinv}</p></div>
    </div>
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">E-Invoice vs Books Reconciliation</h3>
        <ExportMenu reportType="reconciliation" onExport={onExport} testId="export-recon" />
      </div>
      {renderDataContent(loading, reconciliation.length > 0, "Upload E-Invoice report to reconcile",
        <div className="overflow-x-auto max-h-[400px]">
          <table className="data-table text-sm" data-testid="recon-table">
            <thead>
              <tr>
                <th>Invoice</th><th>Party</th><th className="numeric">Books Tax</th><th className="numeric">Books IGST</th>
                <th className="numeric">Books Total</th><th className="numeric">E-Inv Tax</th><th className="numeric">E-Inv IGST</th>
                <th className="numeric">E-Inv Total</th><th className="numeric">Variance</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {reconciliation.map(item => (
                <tr key={item.invoice_number} className={item.status !== 'Matched' ? 'bg-red-50/30' : ''}>
                  <td className="font-mono">{item.invoice_number}</td>
                  <td className="max-w-[120px] truncate">{item.party_name}</td>
                  <td className="numeric font-mono">{item.books_taxable != null ? formatNumber(item.books_taxable) : '\u2014'}</td>
                  <td className="numeric font-mono">{item.books_igst != null ? formatNumber(item.books_igst) : '\u2014'}</td>
                  <td className="numeric font-mono">{item.books_total != null ? formatNumber(item.books_total) : '\u2014'}</td>
                  <td className="numeric font-mono">{item.einv_taxable != null ? formatNumber(item.einv_taxable) : '\u2014'}</td>
                  <td className="numeric font-mono">{item.einv_igst != null ? formatNumber(item.einv_igst) : '\u2014'}</td>
                  <td className="numeric font-mono">{item.einv_total != null ? formatNumber(item.einv_total) : '\u2014'}</td>
                  <td className={`numeric font-mono ${item.variance !== 0 ? 'text-red-600 font-semibold' : ''}`}>{item.variance !== 0 ? formatNumber(item.variance) : '\u2014'}</td>
                  <td>
                    <span className={`badge ${reconStatusBadge(item.status)} inline-flex items-center gap-1`}>
                      {reconStatusIcon(item.status)}{item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);

// ─── Sub-tab: CN/DN (Credit/Debit Notes) ───

const CDNTransactionRow = ({ txn, isSelected, onSelect, onToggleExclude }) => {
  const stateCode = txn.party_gstin ? txn.party_gstin.substring(0, 2) : '';
  const hsnCodes = (txn.inventory_lines || []).map(il => il.hsn_code || il.name || '').filter(Boolean).join(', ') || '\u2014';
  const tv = Math.abs(txn.taxable_value || 0);
  const totalTax = Math.abs(txn.igst || 0) + Math.abs(txn.cgst || 0) + Math.abs(txn.sgst || 0);
  const rate = tv > 0 ? Math.round((totalTax / tv) * 100) : 0;

  return (
    <tr className={`${txn.excluded ? 'opacity-50 bg-slate-50' : ''} ${isSelected ? 'bg-blue-50' : ''}`}>
      <td>
        {!txn.excluded && (
          <input type="checkbox" checked={isSelected} onChange={() => onSelect(txn.id)}
            className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            data-testid={`cdn-select-${txn.id}`} />
        )}
      </td>
      <td><span className={`badge ${txn.voucher_type?.toUpperCase().includes('DEBIT') ? 'badge-b2b-interstate' : 'badge-b2c-local'}`}>{txn.voucher_type}</span></td>
      <td className="font-mono text-xs">{txn.voucher_number}</td>
      <td className="font-mono text-xs">{txn.voucher_date}</td>
      <td className="max-w-[120px] truncate text-xs">{txn.party_name}</td>
      <td className="font-mono text-xs">{txn.party_gstin || '\u2014'}</td>
      <td className="font-mono text-xs">{stateCode || '\u2014'}</td>
      <td><span className={`badge ${categoryBadgeClass[txn.category] || 'badge-exempt'}`}>{txn.category}</span></td>
      <td className="text-xs max-w-[100px] truncate" title={hsnCodes}>{hsnCodes}</td>
      <td className="font-mono text-xs text-center">{rate}%</td>
      <td className="numeric font-mono"><TaxableBreakdown txn={txn} /></td>
      <td className="numeric font-mono">{formatNumber(txn.igst)}</td>
      <td className="numeric font-mono">{formatNumber(txn.cgst)}</td>
      <td className="numeric font-mono">{formatNumber(txn.sgst)}</td>
      <td className="numeric font-mono">{formatNumber(txn.invoice_value)}</td>
      <td>
        <button onClick={() => onToggleExclude(txn.id, !txn.excluded)} className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-sm border transition-colors ${txn.excluded ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'}`} data-testid={`cdn-toggle-${txn.id}`}>
          {txn.excluded ? <><ToggleLeft className="w-3.5 h-3.5" />Excluded</> : <><ToggleRight className="w-3.5 h-3.5" />Included</>}
        </button>
        {txn.exclude_reason && txn.excluded && <span className="text-[10px] text-amber-500 mt-0.5 block">{txn.exclude_reason}</span>}
      </td>
    </tr>
  );
};

const CDNTab = ({ loading, cdnTransactions, onToggleExclude, onBulkExclude, onExport }) => {
  const [vchTypeFilter, setVchTypeFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkReason, setBulkReason] = useState('');
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkExcluding, setBulkExcluding] = useState(false);
  const dnCount = cdnTransactions.filter(t => t.voucher_type?.toUpperCase().includes('DEBIT')).length;
  const cnCount = cdnTransactions.filter(t => t.voucher_type?.toUpperCase().includes('CREDIT')).length;
  const excludedCount = cdnTransactions.filter(t => t.excluded).length;
  const includedTotal = cdnTransactions.filter(t => !t.excluded).reduce((s, t) => s + (t.invoice_value || 0), 0);

  const filteredCdn = useMemo(() => {
    if (vchTypeFilter === 'credit') return cdnTransactions.filter(t => t.voucher_type?.toUpperCase().includes('CREDIT'));
    if (vchTypeFilter === 'debit') return cdnTransactions.filter(t => t.voucher_type?.toUpperCase().includes('DEBIT'));
    return cdnTransactions;
  }, [vchTypeFilter, cdnTransactions]);

  const selectableItems = filteredCdn.filter(t => !t.excluded);
  const allSelected = selectableItems.length > 0 && selectableItems.every(t => selectedIds.has(t.id));

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableItems.map(t => t.id)));
    }
  };

  const handleBulkSubmit = async () => {
    if (!bulkReason.trim() || selectedIds.size === 0) return;
    setBulkExcluding(true);
    const ok = await onBulkExclude([...selectedIds], bulkReason);
    setBulkExcluding(false);
    if (ok) {
      setShowBulkDialog(false);
      setSelectedIds(new Set());
      setBulkReason('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-slate-500">Debit Notes</p><p className="text-lg font-semibold font-mono">{dnCount}</p></div>
        <div className="bg-white border border-slate-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-slate-500">Credit Notes</p><p className="text-lg font-semibold font-mono">{cnCount}</p></div>
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-amber-600">Excluded</p><p className="text-lg font-semibold font-mono text-amber-700">{excludedCount}</p></div>
        <div className="bg-green-50 border border-green-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-green-600">Included Value</p><p className="text-lg font-semibold font-mono text-green-700">{formatNumber(includedTotal)}</p></div>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-slate-800">Credit & Debit Notes ({filteredCdn.length})</h3>
            <select value={vchTypeFilter} onChange={(e) => setVchTypeFilter(e.target.value)} className="text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500" data-testid="cdn-type-filter">
              <option value="all">All Types</option>
              <option value="credit">Credit Notes ({cnCount})</option>
              <option value="debit">Debit Notes ({dnCount})</option>
            </select>
            {selectedIds.size > 0 && (
              <Button
                size="sm"
                onClick={() => setShowBulkDialog(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-xs h-7 px-3"
                data-testid="bulk-exclude-btn"
              >
                Exclude Selected ({selectedIds.size})
              </Button>
            )}
          </div>
          <ExportMenu reportType="cdn" onExport={onExport} testId="export-cdn" />
        </div>
        {renderDataContent(loading, filteredCdn.length > 0, "No Credit/Debit Notes found",
          <div className="overflow-x-auto max-h-[500px]">
            <table className="data-table" data-testid="cdn-table">
              <thead>
                <tr>
                  <th className="w-10">
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                      className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      data-testid="cdn-select-all" />
                  </th>
                  <th>Type</th><th>Number</th><th>Date</th><th>Party</th><th>GSTIN</th><th>State</th><th>Category</th><th>HSN</th><th>Rate</th>
                  <th className="numeric">Taxable</th><th className="numeric">IGST</th><th className="numeric">CGST</th>
                  <th className="numeric">SGST</th><th className="numeric">Total</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredCdn.map(txn => (
                  <CDNTransactionRow key={txn.id} txn={txn} isSelected={selectedIds.has(txn.id)} onSelect={toggleSelect} onToggleExclude={onToggleExclude} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bulk Exclude Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={(open) => !open && setShowBulkDialog(false)}>
        <DialogContent className="sm:max-w-md" aria-describedby="bulk-exclude-desc">
          <DialogHeader><DialogTitle>Exclude {selectedIds.size} CN/DN</DialogTitle></DialogHeader>
          <p id="bulk-exclude-desc" className="sr-only">Provide a reason for excluding selected transactions</p>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-slate-50 rounded-sm text-sm">
              <p><strong>{selectedIds.size}</strong> transaction(s) selected for exclusion</p>
            </div>
            <div>
              <Label>Reason for Exclusion <span className="text-red-500">*</span></Label>
              <textarea
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                placeholder="e.g. Non-income debit notes, Internal adjustments..."
                className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={3}
                data-testid="bulk-exclude-reason-input"
              />
              {bulkReason.trim() === '' && <p className="text-xs text-red-500 mt-1">Reason is mandatory</p>}
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowBulkDialog(false)} className="rounded-sm" data-testid="cancel-bulk-exclude-btn">Cancel</Button>
              <Button
                onClick={handleBulkSubmit}
                disabled={!bulkReason.trim() || bulkExcluding}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm disabled:opacity-50"
                data-testid="confirm-bulk-exclude-btn"
              >
                {bulkExcluding ? 'Excluding...' : `Exclude ${selectedIds.size} items`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Export Menu (Excel + PDF) ───

const ExportMenu = ({ reportType, onExport, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button onClick={() => setOpen(!open)} variant="outline" className="rounded-sm text-xs" data-testid={`${testId}-btn`}>
        <Download className="w-3.5 h-3.5 mr-1.5" />Export
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg z-20 py-1 min-w-[140px]">
          <button onClick={() => { onExport(reportType, 'xlsx'); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center gap-2" data-testid={`${testId}-xlsx`}>
            <FileText className="w-3.5 h-3.5 text-green-600" />Excel (.xlsx)
          </button>
          <button onClick={() => { onExport(reportType, 'pdf'); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center gap-2" data-testid={`${testId}-pdf`}>
            <FileText className="w-3.5 h-3.5 text-red-600" />PDF (.pdf)
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Sub-tab: GSTR-3B ───

const INPUT_TYPES = ["Inputs", "Input Services", "Capital Goods", "Ineligible", "RCM"];
const inputTypeBadge = {
  "Inputs": "bg-blue-100 text-blue-700 border-blue-200",
  "Input Services": "bg-violet-100 text-violet-700 border-violet-200",
  "Capital Goods": "bg-amber-100 text-amber-700 border-amber-200",
  "Ineligible": "bg-red-100 text-red-700 border-red-200",
  "RCM": "bg-teal-100 text-teal-700 border-teal-200",
};

const InputRegisterSection = ({ loading, inputTransactions, filteredInputs, inputStats, inputTypeFilter, setInputTypeFilter, onUpdateInputType, onUpdateGstin, onUpload, uploading, uploadStatus, inputFileRef, onExport }) => (
  <div className="space-y-4">
    {/* Upload section */}
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-sm text-slate-800">Upload Daybook XML for Input Register</h4>
          <p className="text-xs text-slate-500 mt-0.5">Upload one or more Tally daybook XMLs — system extracts vouchers with Input CGST/SGST/IGST</p>
        </div>
        <div className="flex items-center gap-3">
          <input type="file" ref={inputFileRef} accept=".xml" multiple className="hidden" data-testid="input-register-file-input" />
          <Button variant="outline" className="rounded-sm text-sm" onClick={() => inputFileRef.current?.click()} data-testid="input-register-choose-file">
            <FileUp className="w-4 h-4 mr-1.5" />Choose XML
          </Button>
          <Button
            onClick={() => onUpload('input-register', 'input-register', inputFileRef)}
            disabled={uploading['input-register']}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-sm text-sm"
            data-testid="input-register-upload-btn"
          >
            {uploading['input-register'] ? 'Processing...' : 'Upload & Parse'}
          </Button>
        </div>
      </div>
      {uploadStatus['input-register'] && (
        <div className={`mt-3 text-xs px-3 py-2 rounded-sm border ${uploadStatus['input-register'].success ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid="input-register-status">
          {uploadStatus['input-register'].message}
        </div>
      )}
    </div>

    {/* Input Register Stats */}
    {inputTransactions.length > 0 && (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white border border-slate-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-slate-500">Total Entries</p><p className="text-lg font-semibold font-mono">{inputStats.total}</p></div>
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-blue-600">Taxable Value</p><p className="text-lg font-semibold font-mono text-blue-700">{formatNumber(inputStats.taxable)}</p></div>
        <div className="bg-white border border-slate-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-slate-500">IGST</p><p className="text-lg font-semibold font-mono">{formatNumber(inputStats.igst)}</p></div>
        <div className="bg-white border border-slate-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-slate-500">CGST</p><p className="text-lg font-semibold font-mono">{formatNumber(inputStats.cgst)}</p></div>
        <div className="bg-white border border-slate-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-slate-500">SGST</p><p className="text-lg font-semibold font-mono">{formatNumber(inputStats.sgst)}</p></div>
      </div>
    )}

    {/* Input Register Table */}
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-slate-800">Input Register as per Books ({filteredInputs.length})</h3>
          <select value={inputTypeFilter} onChange={(e) => setInputTypeFilter(e.target.value)} className="text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white" data-testid="input-type-filter">
            <option value="all">All Types</option>
            {INPUT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <ExportMenu reportType="gstr3b-input-register" onExport={onExport} testId="export-gstr3b-inputs" />
      </div>
      {renderDataContent(loading, filteredInputs.length > 0, "No input register data. Upload daybook XML to extract input credit entries.",
        <div className="overflow-x-auto max-h-[600px]">
          <table className="data-table" data-testid="input-register-table">
            <thead>
              <tr>
                <th>Date</th><th>Voucher No</th><th>Type</th><th>Party/Supplier</th><th>GSTIN</th>
                <th>Expense Nature</th><th className="numeric">Taxable</th><th className="numeric">IGST</th>
                <th className="numeric">CGST</th><th className="numeric">SGST</th><th className="numeric">Total</th>
                <th>Classification</th>
              </tr>
            </thead>
            <tbody>
              {filteredInputs.map(txn => (
                <InputRegisterRow key={txn.id} txn={txn} onUpdateType={onUpdateInputType} onUpdateGstin={onUpdateGstin} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);

const GSTR3BContent = ({ loading, gstr3bSummary, inputTransactions, inputSummary, onUpdateInputType, onUpdateGstin, onUpload, uploading, uploadStatus, gstr2bEntries, itcRecon, rcmWorking, gstr3bFull, onRunRecon, reconRunning, reconStatus, onOverrideRecon, fetchData, diffTracker, onCarryForward, cfRunning, cfStatus, onResolveDiff, onExport, itcRegister }) => {
  const [gstr3bSubTab, setGstr3bSubTab] = useState('outward');
  const [inputTypeFilter, setInputTypeFilter] = useState('all');

  const filteredInputs = useMemo(() => {
    if (inputTypeFilter === 'all') return inputTransactions;
    return inputTransactions.filter(t => t.input_type === inputTypeFilter);
  }, [inputTransactions, inputTypeFilter]);

  const inputStats = useMemo(() => {
    const s = { total: inputTransactions.length, taxable: 0, igst: 0, cgst: 0, sgst: 0 };
    inputTransactions.forEach(t => { if (t.eligible) { s.taxable += t.taxable_value; s.igst += t.input_igst; s.cgst += t.input_cgst; s.sgst += t.input_sgst; } });
    return s;
  }, [inputTransactions]);

  const inputFileRef = useRef(null);
  const gstr2bFileRef = useRef(null);

  const subTabs = [
    { key: 'outward', label: 'Table 3.1' },
    { key: 'input-register', label: `Inputs (${inputTransactions.length})` },
    { key: 'gstr2b', label: `GSTR-2B (${gstr2bEntries?.length || 0})` },
    { key: 'itc-recon', label: `ITC Running Sheet (${itcRecon?.length || 0})` },
    { key: 'rcm', label: `RCM (${rcmWorking?.count || 0})` },
    { key: 'itc-summary', label: 'Table 4 - ITC' },
    { key: 'itc-register', label: `ITC Register (${itcRegister?.party_count || 0})` },
    { key: 'diff-tracker', label: `Diff Tracker (${diffTracker?.summary?.total_pending || 0})` },
    { key: '3b-summary', label: '3B Summary' },
  ];

  return (
    <div className="space-y-4">
      {/* GSTR-3B Sub-tabs */}
      <div className="flex items-center justify-between border-b border-slate-200" data-testid="gstr3b-subtabs">
        <div className="flex items-center gap-1">
          {subTabs.map(tab => (
            <button key={tab.key} onClick={() => setGstr3bSubTab(tab.key)}
              className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${gstr3bSubTab === tab.key ? 'border-[#0055FF] text-[#0055FF]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid={`gstr3b-subtab-${tab.key}`}
            >{tab.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 pb-1">
          <ExportMenu reportType="gstr3b-complete" onExport={onExport} testId="export-gstr3b-complete" />
        </div>
      </div>

      {/* Outward Supplies - Table 3.1 */}
      {gstr3bSubTab === 'outward' && (
        <GSTR3BOutwardContent loading={loading} gstr3bSummary={gstr3bSummary} />
      )}

      {/* Input Register */}
      {gstr3bSubTab === 'input-register' && (
        <InputRegisterSection
          loading={loading} inputTransactions={inputTransactions}
          filteredInputs={filteredInputs} inputStats={inputStats}
          inputTypeFilter={inputTypeFilter} setInputTypeFilter={setInputTypeFilter}
          onUpdateInputType={onUpdateInputType} onUpdateGstin={onUpdateGstin} onUpload={onUpload}
          uploading={uploading} uploadStatus={uploadStatus}
          inputFileRef={inputFileRef} onExport={onExport}
        />
      )}

      {/* ITC Summary - Table 4 */}
      {gstr3bSubTab === 'itc-summary' && (
        <ITCSummaryContent inputSummary={inputSummary} gstr3bSummary={gstr3bSummary} />
      )}

      {/* GSTR-2B Upload & View */}
      {gstr3bSubTab === 'gstr2b' && (
        <GSTR2BContent
          loading={loading} gstr2bEntries={gstr2bEntries} onUpload={onUpload}
          uploading={uploading} uploadStatus={uploadStatus} fileRef={gstr2bFileRef} onExport={onExport}
        />
      )}

      {/* ITC Reconciliation */}
      {gstr3bSubTab === 'itc-recon' && (
        <ITCReconContent
          loading={loading} itcRecon={itcRecon} onRunRecon={onRunRecon}
          reconRunning={reconRunning} reconStatus={reconStatus} onOverride={onOverrideRecon}
          hasBooks={inputTransactions.length > 0} has2B={(gstr2bEntries?.length || 0) > 0} onExport={onExport}
          onUpload={onUpload} uploading={uploading} uploadStatus={uploadStatus}
        />
      )}

      {/* RCM Working */}
      {gstr3bSubTab === 'rcm' && (
        <RCMWorkingContent loading={loading} rcmWorking={rcmWorking} onRunRecon={onRunRecon} reconRunning={reconRunning} onExport={onExport} />
      )}

      {/* ITC Register (Party × Expense) */}
      {gstr3bSubTab === 'itc-register' && (
        <ITCRegisterContent loading={loading} itcRegister={itcRegister} onExport={onExport} />
      )}

      {/* Difference Tracker */}
      {gstr3bSubTab === 'diff-tracker' && (
        <DiffTrackerContent
          loading={loading} diffTracker={diffTracker} onCarryForward={onCarryForward}
          cfRunning={cfRunning} cfStatus={cfStatus} onResolve={onResolveDiff}
          hasRecon={(itcRecon?.length || 0) > 0} onExport={onExport}
        />
      )}

      {/* 3B Full Summary */}
      {gstr3bSubTab === '3b-summary' && (
        <GSTR3BFullSummary loading={loading} gstr3bFull={gstr3bFull} onExport={onExport} />
      )}
    </div>
  );
};

const ITCRegisterContent = ({ loading, itcRegister, onExport }) => {
  const [expandedParty, setExpandedParty] = useState(null);

  const parties = itcRegister?.parties || [];
  const grandTotal = itcRegister?.grand_total || {};

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-sm text-slate-800">ITC Register (Party × Expense Ledger)</h4>
          <p className="text-xs text-slate-500 mt-0.5">{itcRegister?.party_count || 0} parties, {itcRegister?.entry_count || 0} entries</p>
        </div>
        <ExportMenu reportType="gstr3b-itc-register" onExport={onExport} testId="export-itc-register" />
      </div>

      {/* Grand total summary */}
      {parties.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white border border-slate-200 rounded-sm p-3 text-center">
            <p className="text-xs text-slate-500">Taxable Value</p>
            <p className="text-sm font-semibold text-slate-800">{formatCurrency(grandTotal.taxable_value)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3 text-center">
            <p className="text-xs text-slate-500">IGST</p>
            <p className="text-sm font-semibold text-slate-800">{formatCurrency(grandTotal.igst)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3 text-center">
            <p className="text-xs text-slate-500">CGST</p>
            <p className="text-sm font-semibold text-slate-800">{formatCurrency(grandTotal.cgst)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3 text-center">
            <p className="text-xs text-slate-500">SGST</p>
            <p className="text-sm font-semibold text-slate-800">{formatCurrency(grandTotal.sgst)}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-center">
            <p className="text-xs text-emerald-600">Total ITC</p>
            <p className="text-sm font-semibold text-emerald-700">{formatCurrency(grandTotal.total_tax)}</p>
          </div>
        </div>
      )}

      {/* Party accordion list */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="itc-register-table">
        {renderDataContent(loading, parties.length > 0, "No input register data. Upload the daybook XML first.",
          <div className="divide-y divide-slate-100">
            {parties.map(party => {
              const isExpanded = expandedParty === (party.party_gstin || party.party_name);
              const key = party.party_gstin || party.party_name;
              return (
                <div key={key} data-testid={`itc-register-party-${key}`}>
                  <button
                    onClick={() => setExpandedParty(isExpanded ? null : key)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                    data-testid={`itc-register-toggle-${key}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`text-xs transition-transform ${isExpanded ? 'rotate-90' : ''}`}>&#9654;</span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{party.party_name}</p>
                        <p className="text-xs font-mono text-slate-500">{party.party_gstin || 'No GSTIN'} &middot; {party.ledgers.length} ledger(s)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-right shrink-0">
                      <div className="text-xs text-slate-500">Taxable<br /><span className="font-mono font-medium text-slate-700">{formatNumber(party.totals.taxable_value)}</span></div>
                      <div className="text-xs text-slate-500">Tax<br /><span className="font-mono font-medium text-emerald-700">{formatNumber(party.totals.total_tax)}</span></div>
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-3 bg-slate-50/50">
                      <table className="data-table w-full">
                        <thead>
                          <tr>
                            <th>Expense Ledger</th><th>Voucher</th><th>Date</th>
                            <th className="numeric">Taxable</th><th className="numeric">IGST</th>
                            <th className="numeric">CGST</th><th className="numeric">SGST</th>
                            <th className="numeric">Total Tax</th><th>Rate</th><th>Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {party.ledgers.map(lg => lg.entries.map((entry, ei) => (
                            <tr key={`${lg.ledger_name}-${ei}`}>
                              <td className="text-xs max-w-[180px] truncate" title={lg.ledger_name}>{ei === 0 ? lg.ledger_name : ''}</td>
                              <td className="font-mono text-xs">{entry.voucher_number}</td>
                              <td className="text-xs">{entry.voucher_date}</td>
                              <td className="numeric font-mono">{formatNumber(entry.taxable_value)}</td>
                              <td className="numeric font-mono">{formatNumber(entry.igst)}</td>
                              <td className="numeric font-mono">{formatNumber(entry.cgst)}</td>
                              <td className="numeric font-mono">{formatNumber(entry.sgst)}</td>
                              <td className="numeric font-mono font-medium text-emerald-700">{formatNumber(entry.total_tax)}</td>
                              <td className="text-xs">{entry.gst_rate}%</td>
                              <td className="text-xs">{entry.input_type}</td>
                            </tr>
                          )))}
                          <tr className="bg-slate-100 font-medium">
                            <td className="text-xs italic" colSpan={3}>Subtotal</td>
                            <td className="numeric font-mono">{formatNumber(party.totals.taxable_value)}</td>
                            <td className="numeric font-mono">{formatNumber(party.totals.igst)}</td>
                            <td className="numeric font-mono">{formatNumber(party.totals.cgst)}</td>
                            <td className="numeric font-mono">{formatNumber(party.totals.sgst)}</td>
                            <td className="numeric font-mono font-medium text-emerald-700">{formatNumber(party.totals.total_tax)}</td>
                            <td colSpan={2}></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const InputRegisterRow = ({ txn, onUpdateType, onUpdateGstin }) => {
  const [editing, setEditing] = useState(false);
  const [editingGstin, setEditingGstin] = useState(false);
  const [gstinVal, setGstinVal] = useState(txn.party_gstin || '');

  const handleGstinSave = async () => {
    if (gstinVal.trim() && gstinVal.trim().length !== GSTIN_LENGTH) return;
    const result = await onUpdateGstin(txn.id, gstinVal.trim().toUpperCase());
    setEditingGstin(false);
    if (result?.error) return;
  };

  return (
    <tr className={`${!txn.eligible ? 'opacity-50 bg-slate-50' : ''} ${txn.input_type === 'RCM' ? 'bg-teal-50/30' : ''}`}>
      <td className="font-mono text-xs whitespace-nowrap">{txn.voucher_date}</td>
      <td className="font-mono text-xs">{txn.voucher_number}</td>
      <td className="text-xs">{txn.voucher_type}</td>
      <td className="max-w-[140px] truncate text-xs" title={txn.party_name}>{txn.party_name}</td>
      <td className="font-mono text-xs">
        {editingGstin ? (
          <div className="flex items-center gap-1">
            <input value={gstinVal} onChange={e => setGstinVal(e.target.value.toUpperCase())}
              className="w-[140px] text-xs font-mono border border-slate-300 rounded-sm px-1.5 py-0.5" maxLength={GSTIN_LENGTH}
              placeholder="15-digit GSTIN" data-testid={`gstin-input-${txn.id}`} autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleGstinSave(); if (e.key === 'Escape') setEditingGstin(false); }} />
            <button onClick={handleGstinSave} className="text-xs text-emerald-600 font-medium hover:underline" data-testid={`gstin-save-${txn.id}`}>Save</button>
          </div>
        ) : (
          <button onClick={() => { setGstinVal(txn.party_gstin || ''); setEditingGstin(true); }}
            className={`hover:underline ${txn.party_gstin ? '' : 'text-red-500 font-medium'}`}
            title={txn.party_gstin ? 'Click to edit GSTIN' : 'Click to add GSTIN'}
            data-testid={`gstin-edit-${txn.id}`}>
            {txn.party_gstin || '+ Add GSTIN'}
          </button>
        )}
      </td>
      <td className="max-w-[160px] truncate text-xs" title={txn.expense_nature}>{txn.expense_nature}</td>
      <td className="numeric font-mono">{formatNumber(txn.taxable_value)}</td>
      <td className="numeric font-mono">{formatNumber(txn.input_igst)}</td>
      <td className="numeric font-mono">{formatNumber(txn.input_cgst)}</td>
      <td className="numeric font-mono">{formatNumber(txn.input_sgst)}</td>
      <td className="numeric font-mono">{formatNumber(txn.total_value)}</td>
      <td>
        {editing ? (
          <Select value={txn.input_type} onValueChange={(val) => { onUpdateType(txn.id, val); setEditing(false); }}>
            <SelectTrigger className="h-7 text-xs rounded-sm w-[130px]" data-testid={`input-type-select-${txn.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{INPUT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        ) : (
          <button onClick={() => setEditing(true)} className={`text-xs font-medium px-2 py-1 rounded-sm border ${inputTypeBadge[txn.input_type] || 'bg-slate-100 text-slate-600 border-slate-200'}`} data-testid={`input-type-badge-${txn.id}`}>
            {txn.input_type}
          </button>
        )}
      </td>
    </tr>
  );
};

const GSTR3BOutwardContent = ({ loading, gstr3bSummary }) => (
  <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
      <h3 className="font-semibold text-slate-800">Table 3.1 — Outward Supplies (Auto-pulled from GSTR-1)</h3>
    </div>
    {renderDataContent(loading, gstr3bSummary && Object.keys(gstr3bSummary).length > 0, "No GSTR-1 data available. Upload sales data first.",
      gstr3bSummary && (
      <div className="overflow-x-auto">
        <table className="data-table" data-testid="gstr3b-table31">
          <thead>
            <tr>
              <th className="w-[280px]">Nature of Supply</th>
              <th className="numeric">Taxable Value</th>
              <th className="numeric">IGST</th>
              <th className="numeric">CGST</th>
              <th className="numeric">SGST</th>
              <th className="numeric">Cess</th>
            </tr>
          </thead>
          <tbody>
            {(gstr3bSummary.table_3_1 || []).map((row, i) => (
              <tr key={row.description || `3_1_${i}`} className={row.is_total ? 'font-semibold bg-slate-50 border-t-2 border-slate-300' : ''}>
                <td className={row.is_total ? 'font-semibold' : ''}>{row.description}</td>
                <td className="numeric font-mono">{formatNumber(row.taxable_value)}</td>
                <td className="numeric font-mono">{formatNumber(row.igst)}</td>
                <td className="numeric font-mono">{formatNumber(row.cgst)}</td>
                <td className="numeric font-mono">{formatNumber(row.sgst)}</td>
                <td className="numeric font-mono">{formatNumber(row.cess || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )
    )}
  </div>
);

const ITCSummaryContent = ({ inputSummary, gstr3bSummary }) => {
  if (!inputSummary || !inputSummary.type_breakdown) {
    return <EmptyState message="No input register data. Upload daybook XML to see ITC summary." />;
  }

  const breakdown = inputSummary.type_breakdown;
  const totalItc = inputSummary.total_itc;

  // Get outward tax from Table 3.1 for net liability
  const outwardTax = { igst: 0, cgst: 0, sgst: 0 };
  if (gstr3bSummary?.table_3_1) {
    const totalRow = gstr3bSummary.table_3_1.find(r => r.is_total);
    if (totalRow) { outwardTax.igst = totalRow.igst || 0; outwardTax.cgst = totalRow.cgst || 0; outwardTax.sgst = totalRow.sgst || 0; }
  }

  const netLiability = {
    igst: Math.max(0, outwardTax.igst - (totalItc?.igst || 0)),
    cgst: Math.max(0, outwardTax.cgst - (totalItc?.cgst || 0)),
    sgst: Math.max(0, outwardTax.sgst - (totalItc?.sgst || 0)),
  };

  return (
    <div className="space-y-4">
      {/* Table 4 - ITC Summary */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
          <h3 className="font-semibold text-slate-800">Table 4 — Eligible ITC (as per Books)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="gstr3b-table4">
            <thead>
              <tr><th className="w-[200px]">ITC Type</th><th className="numeric">Count</th><th className="numeric">Taxable</th><th className="numeric">IGST</th><th className="numeric">CGST</th><th className="numeric">SGST</th></tr>
            </thead>
            <tbody>
              {Object.entries(breakdown).map(([type, vals]) => (
                <tr key={type}>
                  <td><span className={`text-xs font-medium px-2 py-1 rounded-sm border ${inputTypeBadge[type] || ''}`}>{type}</span></td>
                  <td className="numeric font-mono">{vals.count}</td>
                  <td className="numeric font-mono">{formatNumber(vals.taxable_value)}</td>
                  <td className="numeric font-mono">{formatNumber(vals.igst)}</td>
                  <td className="numeric font-mono">{formatNumber(vals.cgst)}</td>
                  <td className="numeric font-mono">{formatNumber(vals.sgst)}</td>
                </tr>
              ))}
              <tr className="font-semibold bg-slate-50 border-t-2 border-slate-300">
                <td className="font-semibold">Total ITC Available</td>
                <td></td><td></td>
                <td className="numeric font-mono">{formatNumber(totalItc?.igst)}</td>
                <td className="numeric font-mono">{formatNumber(totalItc?.cgst)}</td>
                <td className="numeric font-mono">{formatNumber(totalItc?.sgst)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Net Liability */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
          <h3 className="font-semibold text-slate-800">Indicative Net Liability</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="gstr3b-net-liability">
            <thead>
              <tr><th className="w-[200px]">Particulars</th><th className="numeric">IGST</th><th className="numeric">CGST</th><th className="numeric">SGST</th></tr>
            </thead>
            <tbody>
              <tr><td>Output Tax Liability</td><td className="numeric font-mono">{formatNumber(outwardTax.igst)}</td><td className="numeric font-mono">{formatNumber(outwardTax.cgst)}</td><td className="numeric font-mono">{formatNumber(outwardTax.sgst)}</td></tr>
              <tr><td>Less: ITC Available</td><td className="numeric font-mono text-green-600">{formatNumber(totalItc?.igst)}</td><td className="numeric font-mono text-green-600">{formatNumber(totalItc?.cgst)}</td><td className="numeric font-mono text-green-600">{formatNumber(totalItc?.sgst)}</td></tr>
              <tr className="font-semibold bg-amber-50 border-t-2 border-amber-300">
                <td className="font-semibold">Net Cash Payable</td>
                <td className="numeric font-mono text-amber-700">{formatNumber(netLiability.igst)}</td>
                <td className="numeric font-mono text-amber-700">{formatNumber(netLiability.cgst)}</td>
                <td className="numeric font-mono text-amber-700">{formatNumber(netLiability.sgst)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── GSTR-2B Content ───

const itcReconStatusBadge = {
  'Matched': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Matched (Value)': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Matched (Prev': 'bg-teal-100 text-teal-700 border-teal-200',
  'Value Mismatch': 'bg-amber-100 text-amber-700 border-amber-200',
  'GST Mismatch': 'bg-amber-100 text-amber-700 border-amber-200',
  'Probable Match': 'bg-violet-100 text-violet-700 border-violet-200',
  'In Books not in 2B': 'bg-red-100 text-red-700 border-red-200',
  'In 2B not in Books': 'bg-blue-100 text-blue-700 border-blue-200',
  'Confirmed': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Accepted': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Rejected': 'bg-red-100 text-red-700 border-red-200',
};

const getItcReconBadgeClass = (status) => {
  if (!status) return 'bg-slate-100 text-slate-600 border-slate-200';
  for (const [key, cls] of Object.entries(itcReconStatusBadge)) {
    if (status.includes(key)) return cls;
  }
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const GSTR2BContent = ({ loading, gstr2bEntries, onUpload, uploading, uploadStatus, fileRef, onExport }) => (
  <div className="space-y-4">
    {/* Upload */}
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-sm text-slate-800">Upload GSTR-2B Excel</h4>
          <p className="text-xs text-slate-500 mt-0.5">Upload one or more GSTR-2B Excel files from GST Portal</p>
        </div>
        <div className="flex items-center gap-3">
          <input type="file" ref={fileRef} accept=".xlsx,.xls" multiple className="hidden" data-testid="gstr2b-file-input" />
          <Button variant="outline" className="rounded-sm text-sm" onClick={() => fileRef.current?.click()} data-testid="gstr2b-choose-file">
            <FileUp className="w-4 h-4 mr-1.5" />Choose Excel
          </Button>
          <Button
            onClick={() => onUpload('gstr2b', 'gstr2b', fileRef)}
            disabled={uploading['gstr2b']}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-sm text-sm"
            data-testid="gstr2b-upload-btn"
          >
            {uploading['gstr2b'] ? 'Processing...' : 'Upload & Parse'}
          </Button>
        </div>
      </div>
      {uploadStatus['gstr2b'] && (
        <div className={`mt-3 text-xs px-3 py-2 rounded-sm border ${uploadStatus['gstr2b'].success ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid="gstr2b-status">
          {uploadStatus['gstr2b'].message}
        </div>
      )}
    </div>

    {/* Stats */}
    {gstr2bEntries?.length > 0 && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-slate-500">Total Entries</p><p className="text-lg font-semibold font-mono" data-testid="gstr2b-total-count">{gstr2bEntries.length}</p></div>
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-blue-600">Normal ITC</p><p className="text-lg font-semibold font-mono text-blue-700">{gstr2bEntries.filter(e => !e.is_rcm).length}</p></div>
        <div className="bg-teal-50 border border-teal-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-teal-600">RCM</p><p className="text-lg font-semibold font-mono text-teal-700">{gstr2bEntries.filter(e => e.is_rcm).length}</p></div>
        <div className="bg-white border border-slate-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-slate-500">Total Tax</p><p className="text-lg font-semibold font-mono">{formatNumber(gstr2bEntries.reduce((s, e) => s + (e.igst || 0) + (e.cgst || 0) + (e.sgst || 0), 0))}</p></div>
      </div>
    )}

    {/* Table */}
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">GSTR-2B Entries ({gstr2bEntries?.length || 0})</h3>
        <ExportMenu reportType="gstr3b-2b-entries" onExport={onExport} testId="export-gstr3b-2b" />
      </div>
      {renderDataContent(loading, gstr2bEntries?.length > 0, "No GSTR-2B data. Upload the Excel file from GST Portal.",
        <div className="overflow-x-auto max-h-[600px]">
          <table className="data-table" data-testid="gstr2b-table">
            <thead>
              <tr>
                <th>GSTIN</th><th>Supplier</th><th>Invoice No</th><th>Invoice Date</th>
                <th className="numeric">Taxable</th><th className="numeric">IGST</th>
                <th className="numeric">CGST</th><th className="numeric">SGST</th>
                <th>RCM</th><th>Sheet</th>
              </tr>
            </thead>
            <tbody>
              {(gstr2bEntries || []).map(e => (
                <tr key={e.id} className={e.is_rcm ? 'bg-teal-50/30' : ''}>
                  <td className="font-mono text-xs">{e.gstin}</td>
                  <td className="max-w-[140px] truncate text-xs" title={e.supplier_name}>{e.supplier_name}</td>
                  <td className="font-mono text-xs">{e.invoice_number}</td>
                  <td className="font-mono text-xs whitespace-nowrap">{e.invoice_date}</td>
                  <td className="numeric font-mono">{formatNumber(e.taxable_value)}</td>
                  <td className="numeric font-mono">{formatNumber(e.igst)}</td>
                  <td className="numeric font-mono">{formatNumber(e.cgst)}</td>
                  <td className="numeric font-mono">{formatNumber(e.sgst)}</td>
                  <td>{e.is_rcm ? <span className="text-xs font-medium px-2 py-0.5 rounded-sm bg-teal-100 text-teal-700 border border-teal-200">Yes</span> : <span className="text-xs text-slate-400">No</span>}</td>
                  <td className="text-xs text-slate-500">{e.sheet}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);

// ─── ITC Reconciliation Content ───

const OVERRIDE_STATUSES = ['Confirmed', 'Accepted', 'Rejected', 'Matched', 'Value Mismatch'];

const ReconStatCard = ({ label, value, colorClass, onClick, testId }) => (
  <div className={`${colorClass} rounded-sm p-3 cursor-pointer hover:border-slate-400`} onClick={onClick} data-testid={testId}>
    <p className="text-xs font-semibold uppercase" style={{ color: 'inherit' }}>{label}</p>
    <p className="text-lg font-semibold font-mono">{value}</p>
  </div>
);

const ITCReconContent = ({ loading, itcRecon, onRunRecon, reconRunning, reconStatus, onOverride, hasBooks, has2B, onExport, onUpload, uploading, uploadStatus }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [claimFilter, setClaimFilter] = useState('all');
  const openingFileRef = useRef(null);

  // Extract unique claim periods for filter dropdown
  const claimPeriods = useMemo(() => {
    const periods = new Set();
    (itcRecon || []).forEach(r => {
      if (r.claim_period) periods.add(r.claim_period);
    });
    return Array.from(periods).sort();
  }, [itcRecon]);

  const filtered = useMemo(() => {
    let list = itcRecon || [];
    if (statusFilter !== 'all') {
      list = list.filter(r => {
        const s = r.user_override || r.status || '';
        if (statusFilter === 'matched') return (s.includes('Matched') && !s.includes('Mismatch') && !s.includes('Probable') && !s.includes('Prev')) || s === 'Confirmed';
        if (statusFilter === 'probable') return s.includes('Probable') && s !== 'Confirmed';
        if (statusFilter === 'mismatch') return s.includes('Mismatch');
        if (statusFilter === 'books-only') return s.includes('In Books');
        if (statusFilter === '2b-only') return s.includes('In 2B');
        if (statusFilter === 'carried-forward') return r.is_carried_forward === true;
        return true;
      });
    }
    if (claimFilter !== 'all') {
      if (claimFilter === 'cf') {
        list = list.filter(r => r.is_carried_forward);
      } else {
        list = list.filter(r => r.claim_period === claimFilter);
      }
    }
    return list;
  }, [itcRecon, statusFilter, claimFilter]);

  const stats = useMemo(() => {
    const s = { matched: 0, probable: 0, mismatch: 0, booksOnly: 0, twobOnly: 0, claimed: 0, carriedForward: 0, total: (itcRecon || []).length };
    (itcRecon || []).forEach(r => {
      const st = r.user_override || r.status || '';
      if (st.includes('Probable') && st !== 'Confirmed') s.probable++;
      else if ((st.includes('Matched') && !st.includes('Mismatch')) || st === 'Confirmed') s.matched++;
      else if (st.includes('In Books')) s.booksOnly++;
      else if (st.includes('In 2B')) s.twobOnly++;
      else s.mismatch++;
      if (r.is_carried_forward) s.carriedForward++;
      else s.claimed++;
    });
    return s;
  }, [itcRecon]);

  return (
    <div className="space-y-4">
      {/* Run Recon button */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-sm text-slate-800">ITC Running Sheet (Books vs GSTR-2B)</h4>
          <p className="text-xs text-slate-500 mt-0.5">FY cumulative reconciliation. Matched entries show claim period; unmatched carry forward automatically.</p>
        </div>
        <Button
          onClick={() => onRunRecon('normal')}
          disabled={reconRunning || (!hasBooks && !has2B)}
          className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-sm text-sm"
          data-testid="run-recon-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${reconRunning ? 'animate-spin' : ''}`} />
          {reconRunning ? 'Running...' : 'Run Reconciliation'}
        </Button>
      </div>
      {reconStatus && (
        <div className={`text-xs px-3 py-2 rounded-sm border ${reconStatus.success ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid="recon-status-msg">
          {reconStatus.message}
        </div>
      )}

      {/* Opening Balance Upload */}
      <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="opening-balance-section">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-sm text-slate-800">Opening Unreconciled Items</h4>
            <p className="text-xs text-slate-500 mt-0.5">Upload carried-forward items from previous period. Each row must specify Source: Not in 2B / Not in Books.</p>
          </div>
          <div className="flex items-center gap-2">
            <a href={`${BACKEND_URL}/api/itc-opening/template`} className="text-xs text-[#0055FF] hover:underline flex items-center gap-1" data-testid="opening-template-link">
              <Download className="w-3 h-3" /> Template
            </a>
            <input ref={openingFileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={() => onUpload('itc-opening', 'itc-opening', openingFileRef)} data-testid="opening-file-input" />
            <Button
              size="sm" variant="outline" className="text-xs"
              onClick={() => openingFileRef.current?.click()}
              disabled={uploading?.['itc-opening']}
              data-testid="opening-upload-btn"
            >
              <FileUp className="w-3.5 h-3.5 mr-1" />
              {uploading?.['itc-opening'] ? 'Uploading...' : 'Upload Opening Items'}
            </Button>
          </div>
        </div>
        {uploadStatus?.['itc-opening'] && (
          <div className={`mt-2 text-xs px-3 py-2 rounded-sm border ${uploadStatus['itc-opening'].success ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid="opening-upload-status">
            {uploadStatus['itc-opening'].message}
          </div>
        )}
      </div>

      {/* Stats */}
      {stats.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <ReconStatCard label="Total" value={stats.total} colorClass="bg-white border border-slate-200" onClick={() => { setStatusFilter('all'); setClaimFilter('all'); }} testId="recon-stat-total" />
          <ReconStatCard label="Matched" value={stats.matched} colorClass="bg-emerald-50 border border-emerald-200 text-emerald-700" onClick={() => { setStatusFilter('matched'); setClaimFilter('all'); }} testId="recon-stat-matched" />
          <ReconStatCard label="Probable" value={stats.probable} colorClass="bg-violet-50 border border-violet-200 text-violet-700" onClick={() => { setStatusFilter('probable'); setClaimFilter('all'); }} testId="recon-stat-probable" />
          <ReconStatCard label="Mismatch" value={stats.mismatch} colorClass="bg-amber-50 border border-amber-200 text-amber-700" onClick={() => { setStatusFilter('mismatch'); setClaimFilter('all'); }} testId="recon-stat-mismatch" />
          <ReconStatCard label="In Books Only" value={stats.booksOnly} colorClass="bg-red-50 border border-red-200 text-red-700" onClick={() => { setStatusFilter('books-only'); setClaimFilter('all'); }} testId="recon-stat-books" />
          <ReconStatCard label="In 2B Only" value={stats.twobOnly} colorClass="bg-blue-50 border border-blue-200 text-blue-700" onClick={() => { setStatusFilter('2b-only'); setClaimFilter('all'); }} testId="recon-stat-2b" />
          <ReconStatCard label="Claimed" value={stats.claimed} colorClass="bg-green-50 border border-green-200 text-green-700" onClick={() => { setStatusFilter('all'); setClaimFilter('all'); }} testId="recon-stat-claimed" />
          <ReconStatCard label="C/F" value={stats.carriedForward} colorClass="bg-orange-50 border border-orange-200 text-orange-700" onClick={() => { setStatusFilter('all'); setClaimFilter('cf'); }} testId="recon-stat-cf" />
        </div>
      )}

      {/* Recon Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Running Sheet ({filtered.length})</h3>
          <div className="flex items-center gap-2">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white" data-testid="recon-filter">
              <option value="all">All Statuses</option>
              <option value="matched">Matched</option>
              <option value="probable">Probable Match</option>
              <option value="mismatch">Mismatch</option>
              <option value="books-only">In Books Only</option>
              <option value="2b-only">In 2B Only</option>
              <option value="carried-forward">Carried Forward</option>
            </select>
            <select value={claimFilter} onChange={e => setClaimFilter(e.target.value)} className="text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white" data-testid="recon-claim-filter">
              <option value="all">All Periods</option>
              <option value="cf">Carried Forward</option>
              {claimPeriods.map(p => (
                <option key={p} value={p}>{formatPeriodLabel(p)}</option>
              ))}
            </select>
            <ExportMenu reportType="gstr3b-itc-recon" onExport={onExport} testId="export-gstr3b-recon" />
          </div>
        </div>
        {renderDataContent(loading, filtered.length > 0, "No reconciliation results. Upload Input Register and GSTR-2B, then click 'Run Reconciliation'.",
          <div className="overflow-x-auto max-h-[600px]">
            <table className="data-table" data-testid="itc-recon-table">
              <thead>
                <tr>
                  <th>GSTIN</th><th>Supplier</th><th>Invoice</th><th>Voucher No</th>
                  <th className="numeric">Books Tax</th><th className="numeric">2B Tax</th>
                  <th className="numeric">Books Val</th><th className="numeric">2B Val</th>
                  <th>Match</th><th>Status</th><th>Origin</th><th>Claim</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <ITCReconRow key={r.id} row={r} onOverride={onOverride} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const ITCReconRow = ({ row, onOverride }) => {
  const [showOverride, setShowOverride] = useState(false);
  const displayStatus = row.user_override || row.status;
  const booksTax = (row.books_igst || 0) + (row.books_cgst || 0) + (row.books_sgst || 0);
  const twobTax = (row.twob_igst || 0) + (row.twob_cgst || 0) + (row.twob_sgst || 0);
  const isProbable = displayStatus?.includes('Probable') && !row.user_override;

  const rowClassName = (() => {
    if (row.is_carried_forward) return 'bg-orange-50/30';
    if (row.status?.includes('In Books')) return 'bg-red-50/30';
    if (row.status?.includes('In 2B')) return 'bg-blue-50/30';
    if (row.status?.includes('Probable')) return 'bg-violet-50/30';
    return '';
  })();

  const matchMethodLabel = (() => {
    if (row.match_method === 'gstin+invoice') return 'GSTIN+Inv';
    if (row.match_method === 'gstin+value') return 'GSTIN+Val';
    if (row.match_method === 'gstin+fuzzy') return 'GSTIN+Fuzzy';
    if (row.match_method === 'invoice+tax') return 'Inv+Tax';
    if (row.match_method === 'name+value') return 'Name+Val';
    if (row.match_method === 'name') return 'Name';
    if (row.match_method === 'amount') return 'Amount';
    if (row.match_method === 'gstin') return 'GSTIN';
    return '\u2014';
  })();

  return (
    <tr className={rowClassName}>
      <td className="font-mono text-xs">{row.gstin}</td>
      <td className="max-w-[120px] truncate text-xs" title={row.supplier_name}>{row.supplier_name}</td>
      <td className="font-mono text-xs">{row.invoice_number}</td>
      <td className="font-mono text-xs text-slate-500">{row.voucher_number || '\u2014'}</td>
      <td className="numeric font-mono">{booksTax ? formatNumber(booksTax) : '\u2014'}</td>
      <td className="numeric font-mono">{twobTax ? formatNumber(twobTax) : '\u2014'}</td>
      <td className="numeric font-mono">{row.books_taxable != null ? formatNumber(row.books_taxable) : '\u2014'}</td>
      <td className="numeric font-mono">{row.twob_taxable != null ? formatNumber(row.twob_taxable) : '\u2014'}</td>
      <td className="text-xs"><span className="text-xs text-slate-500">{matchMethodLabel}</span></td>
      <td>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-sm border ${getItcReconBadgeClass(displayStatus)}`} data-testid={`recon-status-${row.id}`}>
          {displayStatus}
        </span>
        {row.user_override && <span className="ml-1 text-xs text-slate-400" title="Manually overridden">*</span>}
      </td>
      <td className="text-xs text-slate-500 whitespace-nowrap" data-testid={`recon-origin-${row.id}`}>
        {formatPeriodLabel(row.origin_period)}
      </td>
      <td className="whitespace-nowrap" data-testid={`recon-claim-${row.id}`}>
        {row.is_carried_forward ? (
          <span className="text-xs font-medium px-2 py-0.5 rounded-sm bg-orange-100 border border-orange-300 text-orange-700">C/F</span>
        ) : (
          <span className="text-xs font-medium text-green-700">{formatPeriodLabel(row.claim_period)}</span>
        )}
      </td>
      <td>
        <ReconActionCell isProbable={isProbable} showOverride={showOverride} setShowOverride={setShowOverride} row={row} onOverride={onOverride} />
      </td>
    </tr>
  );
};

const ReconActionCell = ({ isProbable, showOverride, setShowOverride, row, onOverride }) => {
  if (isProbable) {
    return (
      <div className="flex items-center gap-1">
        <button onClick={() => onOverride(row.id, 'Confirmed')} className="text-xs font-medium text-emerald-600 hover:underline" data-testid={`recon-confirm-${row.id}`}>Confirm</button>
        <button onClick={() => onOverride(row.id, 'Rejected')} className="text-xs text-red-500 hover:underline" data-testid={`recon-reject-${row.id}`}>Reject</button>
      </div>
    );
  }
  if (showOverride) {
    return (
      <select
        onChange={e => { onOverride(row.id, e.target.value); setShowOverride(false); }}
        className="text-xs border border-slate-300 rounded-sm px-1 py-0.5 bg-white"
        data-testid={`recon-override-${row.id}`}
        defaultValue=""
      >
        <option value="" disabled>Select...</option>
        {OVERRIDE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
    );
  }
  return (
    <button onClick={() => setShowOverride(true)} className="text-xs text-[#0055FF] hover:underline" data-testid={`recon-override-btn-${row.id}`}>
      Override
    </button>
  );
};

// ─── RCM Working Content ───

const rcmCategoryBadge = {
  'Rent': 'bg-purple-100 text-purple-700 border-purple-200',
  'Freight': 'bg-orange-100 text-orange-700 border-orange-200',
  'Legal': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Partner Remuneration': 'bg-pink-100 text-pink-700 border-pink-200',
  'Director Remuneration': 'bg-pink-100 text-pink-700 border-pink-200',
  'Other': 'bg-slate-100 text-slate-600 border-slate-200',
};

const RCMWorkingContent = ({ loading, rcmWorking, onRunRecon, reconRunning, onExport }) => {
  const entries = rcmWorking?.entries || [];
  const totals = rcmWorking?.totals || {};

  return (
    <div className="space-y-4">
      {/* RCM Totals */}
      {entries.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white border border-slate-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-slate-500">RCM Entries</p><p className="text-lg font-semibold font-mono" data-testid="rcm-count">{entries.length}</p></div>
          <div className="bg-teal-50 border border-teal-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-teal-600">Taxable Value</p><p className="text-lg font-semibold font-mono text-teal-700">{formatNumber(totals.taxable_value)}</p></div>
          <div className="bg-white border border-slate-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-slate-500">IGST</p><p className="text-lg font-semibold font-mono">{formatNumber(totals.igst)}</p></div>
          <div className="bg-white border border-slate-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-slate-500">CGST</p><p className="text-lg font-semibold font-mono">{formatNumber(totals.cgst)}</p></div>
          <div className="bg-white border border-slate-200 rounded-sm p-3"><p className="text-xs font-semibold uppercase text-slate-500">SGST</p><p className="text-lg font-semibold font-mono">{formatNumber(totals.sgst)}</p></div>
        </div>
      )}

      {/* RCM Recon button */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-sm text-slate-800">RCM Liability (Reverse Charge)</h4>
          <p className="text-xs text-slate-500 mt-0.5">RCM entries classified from Input Register (Type = RCM)</p>
        </div>
        <Button
          onClick={() => onRunRecon('rcm')}
          disabled={reconRunning || entries.length === 0}
          variant="outline"
          className="rounded-sm text-sm"
          data-testid="run-rcm-recon-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${reconRunning ? 'animate-spin' : ''}`} />
          Reconcile RCM with 2B
        </Button>
      </div>

      {/* RCM Entries Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">RCM Working ({entries.length})</h3>
          <ExportMenu reportType="gstr3b-rcm" onExport={onExport} testId="export-gstr3b-rcm" />
        </div>
        {renderDataContent(loading, entries.length > 0, "No RCM entries found. Classify input register entries as 'RCM' type to see them here.",
          <div className="overflow-x-auto max-h-[600px]">
            <table className="data-table" data-testid="rcm-table">
              <thead>
                <tr>
                  <th>Date</th><th>Voucher No</th><th>Party</th><th>Ledger</th>
                  <th>Category</th><th className="numeric">Taxable</th>
                  <th className="numeric">IGST</th><th className="numeric">CGST</th><th className="numeric">SGST</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id}>
                    <td className="font-mono text-xs whitespace-nowrap">{e.voucher_date}</td>
                    <td className="font-mono text-xs">{e.voucher_number}</td>
                    <td className="max-w-[140px] truncate text-xs" title={e.party_name}>{e.party_name}</td>
                    <td className="max-w-[160px] truncate text-xs" title={e.ledger_name}>{e.ledger_name}</td>
                    <td><span className={`text-xs font-medium px-2 py-0.5 rounded-sm border ${rcmCategoryBadge[e.rcm_category] || rcmCategoryBadge['Other']}`}>{e.rcm_category}</span></td>
                    <td className="numeric font-mono">{formatNumber(e.taxable_value)}</td>
                    <td className="numeric font-mono">{formatNumber(e.input_igst)}</td>
                    <td className="numeric font-mono">{formatNumber(e.input_cgst)}</td>
                    <td className="numeric font-mono">{formatNumber(e.input_sgst)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── GSTR-3B Full Summary ───

const GSTR3BFullSummary = ({ loading, gstr3bFull, onExport }) => {
  if (!gstr3bFull) return <EmptyState message="No data available. Upload sales data and input register to see the GSTR-3B summary." />;

  const { table_3_1, rcm_liability, table_4, itc_reversed, net_itc, recon_stats, tax_payment } = gstr3bFull;

  return (
    <div className="space-y-5">
      {/* KPI Cards */}
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <p className="text-xs font-semibold uppercase text-slate-500">Output Liability</p>
          <p className="text-xl font-bold font-mono mt-1" data-testid="3b-output-liability">{formatNumber((tax_payment?.output_liability?.igst || 0) + (tax_payment?.output_liability?.cgst || 0) + (tax_payment?.output_liability?.sgst || 0))}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
          <p className="text-xs font-semibold uppercase text-emerald-600">ITC Utilized</p>
          <p className="text-xl font-bold font-mono text-emerald-700 mt-1" data-testid="3b-itc-utilized">{formatNumber((tax_payment?.itc_utilized?.igst || 0) + (tax_payment?.itc_utilized?.cgst || 0) + (tax_payment?.itc_utilized?.sgst || 0))}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
          <p className="text-xs font-semibold uppercase text-amber-600">Cash Payable</p>
          <p className="text-xl font-bold font-mono text-amber-700 mt-1" data-testid="3b-cash-payable">{formatNumber((tax_payment?.cash_payable?.igst || 0) + (tax_payment?.cash_payable?.cgst || 0) + (tax_payment?.cash_payable?.sgst || 0))}</p>
        </div>
        <div className="bg-teal-50 border border-teal-200 rounded-sm p-4">
          <p className="text-xs font-semibold uppercase text-teal-600">RCM Liability</p>
          <p className="text-xl font-bold font-mono text-teal-700 mt-1" data-testid="3b-rcm-liability">{formatNumber((rcm_liability?.igst || 0) + (rcm_liability?.cgst || 0) + (rcm_liability?.sgst || 0))}</p>
        </div>
      </div>
        <div className="flex items-center ml-3 pb-2">
          <ExportMenu reportType="gstr3b-summary" onExport={onExport} testId="export-gstr3b-summary" />
        </div>
      </div>

      {/* Recon Stats */}
      {recon_stats?.total > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h4 className="font-semibold text-sm text-slate-800 mb-3">ITC Reconciliation Status</h4>
          <div className="flex gap-6 text-sm">
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-500"></span><span className="text-slate-600">Matched: <strong className="font-mono">{recon_stats.matched}</strong></span></div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-amber-500"></span><span className="text-slate-600">Mismatch: <strong className="font-mono">{recon_stats.value_mismatch}</strong></span></div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-500"></span><span className="text-slate-600">In Books Only: <strong className="font-mono">{recon_stats.in_books_not_2b}</strong></span></div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-blue-500"></span><span className="text-slate-600">In 2B Only: <strong className="font-mono">{recon_stats.in_2b_not_books}</strong></span></div>
          </div>
        </div>
      )}

      {/* Table 3.1 */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
          <h3 className="font-semibold text-slate-800">Table 3.1 — Outward Supplies & RCM Liability</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="3b-summary-table31">
            <thead>
              <tr><th className="w-[280px]">Nature of Supply</th><th className="numeric">Taxable</th><th className="numeric">IGST</th><th className="numeric">CGST</th><th className="numeric">SGST</th><th className="numeric">Cess</th></tr>
            </thead>
            <tbody>
              {(table_3_1 || []).map((row, i) => (
                <tr key={row.description || `t31_${i}`} className={row.is_total ? 'font-semibold bg-slate-50 border-t-2 border-slate-300' : ''}>
                  <td className={row.is_total ? 'font-semibold' : ''}>{row.description}</td>
                  <td className="numeric font-mono">{formatNumber(row.taxable_value)}</td>
                  <td className="numeric font-mono">{formatNumber(row.igst)}</td>
                  <td className="numeric font-mono">{formatNumber(row.cgst)}</td>
                  <td className="numeric font-mono">{formatNumber(row.sgst)}</td>
                  <td className="numeric font-mono">{formatNumber(row.cess || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Table 4 - ITC */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
          <h3 className="font-semibold text-slate-800">Table 4 — ITC Available</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="3b-summary-table4">
            <thead>
              <tr><th className="w-[280px]">Description</th><th className="numeric">IGST</th><th className="numeric">CGST</th><th className="numeric">SGST</th></tr>
            </thead>
            <tbody>
              {(table_4 || []).map((row, i) => (
                <tr key={row.description || `t4_${i}`} className={row.is_total ? 'font-semibold bg-slate-50 border-t-2 border-slate-300' : ''}>
                  <td className={row.is_total ? 'font-semibold' : ''}>{row.description}</td>
                  <td className="numeric font-mono">{formatNumber(row.igst)}</td>
                  <td className="numeric font-mono">{formatNumber(row.cgst)}</td>
                  <td className="numeric font-mono">{formatNumber(row.sgst)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ITC Reversed */}
      {itc_reversed?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
            <h3 className="font-semibold text-slate-800">ITC Reversed / Ineligible</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="3b-itc-reversed">
              <thead>
                <tr><th className="w-[280px]">Description</th><th className="numeric">IGST</th><th className="numeric">CGST</th><th className="numeric">SGST</th></tr>
              </thead>
              <tbody>
                {itc_reversed.map((row, i) => (
                  <tr key={row.description || `rev_${i}`}>
                    <td>{row.description}</td>
                    <td className="numeric font-mono">{formatNumber(row.igst)}</td>
                    <td className="numeric font-mono">{formatNumber(row.cgst)}</td>
                    <td className="numeric font-mono">{formatNumber(row.sgst)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tax Payment */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
          <h3 className="font-semibold text-slate-800">Section 6 — Tax Payment Summary</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="3b-tax-payment">
            <thead>
              <tr><th className="w-[280px]">Particulars</th><th className="numeric">IGST</th><th className="numeric">CGST</th><th className="numeric">SGST</th><th className="numeric">Total</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Output Tax Liability</td>
                <td className="numeric font-mono">{formatNumber(tax_payment?.output_liability?.igst)}</td>
                <td className="numeric font-mono">{formatNumber(tax_payment?.output_liability?.cgst)}</td>
                <td className="numeric font-mono">{formatNumber(tax_payment?.output_liability?.sgst)}</td>
                <td className="numeric font-mono font-semibold">{formatNumber((tax_payment?.output_liability?.igst || 0) + (tax_payment?.output_liability?.cgst || 0) + (tax_payment?.output_liability?.sgst || 0))}</td>
              </tr>
              <tr>
                <td className="text-emerald-700">Less: ITC Utilized</td>
                <td className="numeric font-mono text-emerald-600">{formatNumber(tax_payment?.itc_utilized?.igst)}</td>
                <td className="numeric font-mono text-emerald-600">{formatNumber(tax_payment?.itc_utilized?.cgst)}</td>
                <td className="numeric font-mono text-emerald-600">{formatNumber(tax_payment?.itc_utilized?.sgst)}</td>
                <td className="numeric font-mono text-emerald-600 font-semibold">{formatNumber((tax_payment?.itc_utilized?.igst || 0) + (tax_payment?.itc_utilized?.cgst || 0) + (tax_payment?.itc_utilized?.sgst || 0))}</td>
              </tr>
              <tr className="font-semibold bg-amber-50 border-t-2 border-amber-300">
                <td className="font-semibold text-amber-800">Net Cash Payable</td>
                <td className="numeric font-mono text-amber-700">{formatNumber(tax_payment?.cash_payable?.igst)}</td>
                <td className="numeric font-mono text-amber-700">{formatNumber(tax_payment?.cash_payable?.cgst)}</td>
                <td className="numeric font-mono text-amber-700">{formatNumber(tax_payment?.cash_payable?.sgst)}</td>
                <td className="numeric font-mono text-amber-700 font-bold">{formatNumber((tax_payment?.cash_payable?.igst || 0) + (tax_payment?.cash_payable?.cgst || 0) + (tax_payment?.cash_payable?.sgst || 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Difference Tracker Content ───

const agingBadge = (months) => {
  if (months <= 1) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (months === 2) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-red-100 text-red-700 border-red-200';
};

const diffTypeBadge = {
  'in_books_not_2b': 'bg-red-100 text-red-700 border-red-200',
  'in_2b_not_books': 'bg-blue-100 text-blue-700 border-blue-200',
  'mismatch': 'bg-amber-100 text-amber-700 border-amber-200',
};

const diffTypeLabel = {
  'in_books_not_2b': 'Books Only',
  'in_2b_not_books': '2B Only',
  'mismatch': 'Mismatch',
};

const DiffTrackerContent = ({ loading, diffTracker, onCarryForward, cfRunning, cfStatus, onResolve, hasRecon, onExport }) => {
  const [statusFilter, setStatusFilter] = useState('pending');
  const summary = diffTracker?.summary || {};
  const pending = diffTracker?.pending || [];
  const resolved = diffTracker?.resolved || [];

  const displayItems = statusFilter === 'resolved' ? resolved : pending;

  return (
    <div className="space-y-4">
      {/* Action Bar */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-sm text-slate-800">Difference Tracker / Carry Forward</h4>
          <p className="text-xs text-slate-500 mt-0.5">Track unresolved ITC mismatches across months until resolved</p>
        </div>
        <Button
          onClick={onCarryForward}
          disabled={cfRunning || !hasRecon}
          className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-sm text-sm"
          data-testid="carry-forward-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${cfRunning ? 'animate-spin' : ''}`} />
          {cfRunning ? 'Processing...' : 'Run Carry Forward'}
        </Button>
      </div>
      {cfStatus && (
        <div className={`text-xs px-3 py-2 rounded-sm border ${cfStatus.success ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid="cf-status-msg">
          {cfStatus.message}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <p className="text-xs font-semibold uppercase text-slate-500">Total Pending</p>
          <p className="text-xl font-bold font-mono" data-testid="diff-total-pending">{summary.total_pending || 0}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
          <p className="text-xs font-semibold uppercase text-emerald-600">Resolved This Month</p>
          <p className="text-xl font-bold font-mono text-emerald-700" data-testid="diff-resolved-month">{summary.resolved_this_month || 0}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 cursor-pointer" onClick={() => setStatusFilter('pending')}>
          <p className="text-xs font-semibold uppercase text-emerald-600">Age 1M</p>
          <p className="text-xl font-bold font-mono text-emerald-700">{summary.aging_1m || 0}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 cursor-pointer" onClick={() => setStatusFilter('pending')}>
          <p className="text-xs font-semibold uppercase text-amber-600">Age 2M</p>
          <p className="text-xl font-bold font-mono text-amber-700">{summary.aging_2m || 0}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-sm p-3 cursor-pointer" onClick={() => setStatusFilter('pending')}>
          <p className="text-xs font-semibold uppercase text-red-600">{"Age 3M+"}</p>
          <p className="text-xl font-bold font-mono text-red-700">{summary.aging_3plus || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <p className="text-xs font-semibold uppercase text-slate-500">Pending Tax</p>
          <p className="text-lg font-bold font-mono">{formatNumber(summary.total_pending_tax || 0)}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">{statusFilter === 'resolved' ? `Resolved (${resolved.length})` : `Pending Differences (${pending.length})`}</h3>
          <div className="flex items-center gap-2">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white" data-testid="diff-filter">
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
            </select>
            <ExportMenu reportType="gstr3b-diff-tracker" onExport={onExport} testId="export-gstr3b-diff" />
          </div>
        </div>
        {renderDataContent(loading, displayItems.length > 0,
          statusFilter === 'resolved' ? "No resolved differences yet." : "No pending differences. Run 'Carry Forward' after ITC Reconciliation to track mismatches.",
          <div className="overflow-x-auto max-h-[600px]">
            <table className="data-table" data-testid="diff-tracker-table">
              <thead>
                <tr>
                  <th>GSTIN</th><th>Supplier</th><th>Invoice</th><th>Origin</th>
                  {statusFilter === 'pending' && <th>Age</th>}
                  <th>Type</th>
                  <th className="numeric">Books Tax</th><th className="numeric">2B Tax</th>
                  {statusFilter === 'resolved' && <th>Resolved</th>}
                  {statusFilter === 'pending' && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {displayItems.map(item => (
                  <DiffTrackerRow key={item.id} item={item} showResolve={statusFilter === 'pending'} onResolve={onResolve} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const DiffTrackerRow = ({ item, showResolve, onResolve }) => {
  const [resolving, setResolving] = useState(false);
  const [remarks, setRemarks] = useState('');
  const booksTax = (item.books_igst || 0) + (item.books_cgst || 0) + (item.books_sgst || 0);
  const twobTax = (item.twob_igst || 0) + (item.twob_cgst || 0) + (item.twob_sgst || 0);

  const rowClass = (() => {
    if (item.aging_months >= 3) return 'bg-red-50/30';
    if (item.aging_months === 2) return 'bg-amber-50/20';
    return '';
  })();

  return (
    <tr className={rowClass}>
      <td className="font-mono text-xs">{item.gstin}</td>
      <td className="max-w-[120px] truncate text-xs" title={item.supplier_name}>{item.supplier_name}</td>
      <td className="font-mono text-xs">{item.invoice_number}</td>
      <td className="font-mono text-xs whitespace-nowrap">{item.origin_period}</td>
      {showResolve && (
        <td><span className={`text-xs font-medium px-2 py-0.5 rounded-sm border ${agingBadge(item.aging_months || 0)}`}>{item.aging_months || 0}M</span></td>
      )}
      <td><span className={`text-xs font-medium px-2 py-0.5 rounded-sm border ${diffTypeBadge[item.diff_type] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{diffTypeLabel[item.diff_type] || item.diff_type}</span></td>
      <td className="numeric font-mono">{booksTax ? formatNumber(booksTax) : '\u2014'}</td>
      <td className="numeric font-mono">{twobTax ? formatNumber(twobTax) : '\u2014'}</td>
      {!showResolve && (
        <td className="text-xs text-slate-500">{item.resolved_period}{item.resolve_remarks ? ` — ${item.resolve_remarks}` : ''}</td>
      )}
      {showResolve && (
        <td>
          {resolving ? (
            <div className="flex items-center gap-1">
              <input value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Remarks..." className="text-xs border border-slate-300 rounded-sm px-1.5 py-0.5 w-[100px]" data-testid={`diff-remarks-${item.id}`} />
              <button onClick={() => { onResolve(item.id, remarks); setResolving(false); }} className="text-xs text-emerald-600 font-medium hover:underline" data-testid={`diff-confirm-${item.id}`}>OK</button>
              <button onClick={() => setResolving(false)} className="text-xs text-slate-400 hover:underline">X</button>
            </div>
          ) : (
            <button onClick={() => setResolving(true)} className="text-xs text-[#0055FF] hover:underline" data-testid={`diff-resolve-btn-${item.id}`}>Resolve</button>
          )}
        </td>
      )}
    </tr>
  );
};

// ─── Filing Status Tab ───

function getFilingCardStyle(filed, isDelayed, isPastDue) {
  if (filed && isDelayed) return { border: 'border-red-300 bg-red-50/30', header: 'bg-red-50 border-red-200', icon: 'text-red-600' };
  if (filed) return { border: 'border-emerald-300 bg-emerald-50/30', header: 'bg-emerald-50 border-emerald-200', icon: 'text-emerald-600' };
  if (isPastDue) return { border: 'border-red-300', header: 'bg-slate-50 border-slate-100', icon: 'text-slate-400' };
  return { border: 'border-slate-200', header: 'bg-slate-50 border-slate-100', icon: 'text-slate-400' };
}

function getFilingBadge(filed, isDelayed, isPastDue) {
  if (filed && isDelayed) return { className: 'bg-red-100 text-red-700 border-red-300', label: 'Delayed', Icon: AlertTriangle };
  if (filed) return { className: 'bg-emerald-100 text-emerald-700 border-emerald-300', label: 'Filed', Icon: CheckCircle };
  if (isPastDue) return { className: 'bg-red-100 text-red-700 border-red-300', label: 'Overdue', Icon: Clock };
  return { className: 'bg-slate-100 text-slate-500 border-slate-300', label: 'Not Filed', Icon: Clock };
}

const FiledReturnView = ({ filingData, returnType, onDownload, onDelete }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Date of Filing</p>
        <p className="text-sm font-mono text-slate-900" data-testid={`filing-date-display-${returnType}`}>
          {new Date(filingData.filing_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
        </p>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Acknowledgement No.</p>
        <p className="text-sm font-mono text-slate-900" data-testid={`filing-ack-display-${returnType}`}>{filingData.acknowledgement_number}</p>
      </div>
    </div>
    <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
      <Button variant="outline" className="rounded-sm text-xs" onClick={onDownload} data-testid={`filing-download-pdf-${returnType}`}>
        <FileText className="w-3.5 h-3.5 mr-1.5 text-red-500" />Download PDF
      </Button>
      <Button variant="outline" className="rounded-sm text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={onDelete} data-testid={`filing-delete-${returnType}`}>
        <Trash2 className="w-3.5 h-3.5 mr-1.5" />Remove
      </Button>
      <span className="text-[10px] text-slate-400 ml-auto">
        Uploaded by {filingData.uploaded_by} on {new Date(filingData.uploaded_at).toLocaleDateString('en-IN')}
      </span>
    </div>
  </div>
);

const UnfiledReturnForm = ({ fileRef, filingDate, setFilingDate, ackNumber, setAckNumber, error, uploading, onSubmit, returnType }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Date of Filing <span className="text-red-500">*</span></label>
        <input type="date" value={filingDate} onChange={(e) => setFilingDate(e.target.value)}
          className="w-full border border-slate-300 rounded-sm text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid={`filing-date-input-${returnType}`} />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Acknowledgement No. <span className="text-red-500">*</span></label>
        <input type="text" value={ackNumber} onChange={(e) => setAckNumber(e.target.value)}
          placeholder="e.g. AA2702261234567"
          className="w-full border border-slate-300 rounded-sm text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
          data-testid={`filing-ack-input-${returnType}`} />
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">Return PDF <span className="text-red-500">*</span></label>
      <div className="flex items-center gap-3">
        <input type="file" ref={fileRef} accept=".pdf" className="hidden" data-testid={`filing-file-input-${returnType}`} />
        <Button variant="outline" className="rounded-sm text-sm" onClick={() => fileRef.current?.click()} data-testid={`filing-choose-file-${returnType}`}>
          <FileUp className="w-4 h-4 mr-1.5" />Choose PDF
        </Button>
        <span className="text-xs text-slate-500">{fileRef.current?.files?.[0]?.name || 'No file selected'}</span>
      </div>
    </div>
    {error && (
      <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid={`filing-error-${returnType}`}>
        <XCircle className="w-3.5 h-3.5" />{error}
      </div>
    )}
    <Button onClick={onSubmit} disabled={uploading} className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-sm" data-testid={`filing-submit-${returnType}`}>
      {uploading ? 'Uploading...' : <><CalendarCheck className="w-4 h-4 mr-1.5" />Record Filing</>}
    </Button>
  </div>
);


const FilingReturnCard = ({ returnType, label, dueDay, filingData, clientId, yearInt, monthInt, onUploaded }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [filingDate, setFilingDate] = useState('');
  const [ackNumber, setAckNumber] = useState('');
  const [error, setError] = useState('');

  const filed = filingData?.filed;
  const dueDate = filingData?.due_date;
  const dueDateDisplay = dueDate ? new Date(dueDate + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const today = new Date().toISOString().split('T')[0];
  const isPastDue = dueDate && !filed && today > dueDate;
  const isDelayed = filingData?.is_delayed;

  const cardStyle = getFilingCardStyle(filed, isDelayed, isPastDue);
  const badge = getFilingBadge(filed, isDelayed, isPastDue);

  const handleSubmit = async () => {
    if (!fileRef.current?.files?.length) { setError('Please select a PDF file'); return; }
    if (!filingDate) { setError('Filing date is required'); return; }
    if (!ackNumber.trim()) { setError('Acknowledgement number is required'); return; }
    setError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', fileRef.current.files[0]);
      formData.append('filing_date', filingDate);
      formData.append('acknowledgement_number', ackNumber.trim());
      await axios.post(`${BACKEND_URL}/api/filings/${clientId}/${yearInt}/${monthInt}/${returnType}`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove ${label} filing record?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/filings/${clientId}/${yearInt}/${monthInt}/${returnType}`, { withCredentials: true });
      onUploaded();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete filing');
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/filings/${clientId}/${yearInt}/${monthInt}/${returnType}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filingData?.pdf_filename || `${returnType}_filing.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to download PDF');
    }
  };

  return (
    <div className={`bg-white border rounded-sm ${cardStyle.border}`} data-testid={`filing-card-${returnType}`}>
      {/* Header */}
      <div className={`px-5 py-3 border-b flex items-center justify-between ${cardStyle.header}`}>
        <div className="flex items-center gap-2">
          <ShieldCheck className={`w-5 h-5 ${cardStyle.icon}`} />
          <h3 className="font-semibold text-slate-900">{label}</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 font-mono">Due: {dueDateDisplay}</span>
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-sm border ${badge.className}`} data-testid={`filing-badge-${returnType}`}>
            <badge.Icon className="w-3 h-3" /> {badge.label}
          </span>
        </div>
      </div>

      <div className="p-5">
        {filed
          ? <FiledReturnView filingData={filingData} returnType={returnType} onDownload={handleDownloadPdf} onDelete={handleDelete} />
          : <UnfiledReturnForm fileRef={fileRef} filingDate={filingDate} setFilingDate={setFilingDate} ackNumber={ackNumber} setAckNumber={setAckNumber} error={error} uploading={uploading} onSubmit={handleSubmit} returnType={returnType} />
        }
      </div>
    </div>
  );
};

// ─── Anomaly Detection Tab ───

const ANOMALY_TYPE_META = {
  duplicate_gstin: { label: 'Duplicate GSTIN', color: 'bg-red-50 text-red-700 border-red-200' },
  tax_rate_deviation: { label: 'Tax Rate Deviation', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  missing_gstin: { label: 'Missing GSTIN (B2B)', color: 'bg-red-50 text-red-700 border-red-200' },
  duplicate_invoice: { label: 'Duplicate Invoice', color: 'bg-red-50 text-red-700 border-red-200' },
};

const AnomalySeverityBadge = ({ severity }) => {
  const cls = severity === 'high' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800';
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase rounded ${cls}`} data-testid={`severity-${severity}`}>{severity}</span>;
};

const AnomalyCard = ({ anomaly }) => {
  const meta = ANOMALY_TYPE_META[anomaly.type] || { label: anomaly.type, color: 'bg-slate-50 text-slate-700 border-slate-200' };
  const details = anomaly.details || {};
  return (
    <div className={`border rounded-sm p-3 ${meta.color}`} data-testid={`anomaly-card-${anomaly.type}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold">{meta.label}</span>
            <AnomalySeverityBadge severity={anomaly.severity} />
          </div>
          <p className="text-sm font-medium">{anomaly.description}</p>
          <p className="text-xs font-mono mt-1 opacity-80">{anomaly.gstin}</p>
        </div>
      </div>
      {anomaly.type === 'duplicate_gstin' && details.party_names && (
        <div className="mt-2 text-xs space-y-0.5">
          {details.party_names.map((n, i) => <div key={`party-${n}-${i}`} className="font-mono">{i + 1}. {n}</div>)}
        </div>
      )}
      {anomaly.type === 'tax_rate_deviation' && (
        <div className="mt-2 text-xs grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono">
          <span>Party: {details.party_name}</span>
          <span>Voucher: {details.voucher_number}</span>
          <span>Taxable: {formatCurrency(details.taxable_value)}</span>
          <span>Effective Rate: {details.effective_rate}%</span>
        </div>
      )}
      {anomaly.type === 'missing_gstin' && (
        <div className="mt-2 text-xs font-mono">
          {details.party_name} | {details.voucher_number} | {formatCurrency(details.invoice_value)}
        </div>
      )}
      {anomaly.type === 'duplicate_invoice' && (
        <div className="mt-2 text-xs font-mono">
          {details.party_name} | Inv#{details.invoice_number} x{details.count} | Total: {formatCurrency(details.total_value)}
        </div>
      )}
    </div>
  );
};

const AnomaliesTab = ({ anomalies, loading, onExport }) => {
  const [typeFilter, setTypeFilter] = useState('all');

  const filtered = useMemo(() => {
    if (!anomalies?.anomalies) return [];
    if (typeFilter === 'all') return anomalies.anomalies;
    return anomalies.anomalies.filter(a => a.type === typeFilter);
  }, [anomalies, typeFilter]);

  if (loading) return <LoadingState />;
  if (!anomalies) return <div className="text-sm text-slate-500 p-4" data-testid="anomalies-empty">No anomaly data available. Upload transactions first.</div>;

  const total = anomalies.total || 0;
  const byType = anomalies.by_type || {};

  return (
    <div className="space-y-4" data-testid="anomalies-tab">
      {/* Overview cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Total Anomalies</p>
          <p className={`text-2xl font-bold font-mono ${total > 0 ? 'text-red-600' : 'text-green-600'}`} data-testid="anomalies-total">{total}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <p className="text-xs text-slate-500 uppercase tracking-wide">High Severity</p>
          <p className="text-2xl font-bold font-mono text-red-600" data-testid="anomalies-high">{anomalies.high_severity || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Medium Severity</p>
          <p className="text-2xl font-bold font-mono text-amber-600" data-testid="anomalies-medium">{anomalies.medium_severity || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Duplicate GSTINs</p>
          <p className="text-2xl font-bold font-mono" data-testid="anomalies-dup-gstin">{byType.duplicate_gstin || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Rate Deviations</p>
          <p className="text-2xl font-bold font-mono" data-testid="anomalies-rate-dev">{byType.tax_rate_deviation || 0}</p>
        </div>
      </div>

      {/* Filter + Export */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-48 rounded-sm" data-testid="anomaly-type-filter"><SelectValue placeholder="Filter by type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types ({total})</SelectItem>
              <SelectItem value="duplicate_gstin">Duplicate GSTIN ({byType.duplicate_gstin || 0})</SelectItem>
              <SelectItem value="tax_rate_deviation">Tax Rate Deviation ({byType.tax_rate_deviation || 0})</SelectItem>
              <SelectItem value="missing_gstin">Missing GSTIN ({byType.missing_gstin || 0})</SelectItem>
              <SelectItem value="duplicate_invoice">Duplicate Invoice ({byType.duplicate_invoice || 0})</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-500">{filtered.length} showing</span>
        </div>
        <Button variant="outline" className="rounded-sm" onClick={() => onExport('anomalies')} data-testid="export-anomalies-btn">
          <Download className="w-4 h-4 mr-1.5" />Export Anomalies
        </Button>
      </div>

      {/* Results */}
      {total === 0 ? (
        <div className="text-center py-12 text-slate-500" data-testid="anomalies-clean">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
          <p className="text-lg font-semibold text-slate-700">No anomalies detected</p>
          <p className="text-sm">All transactions look clean for this period.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((a) => <AnomalyCard key={a.id || `${a.type}-${a.gstin}`} anomaly={a} />)}
        </div>
      )}
    </div>
  );
};

const FilingStatusTab = ({ clientId, yearInt, monthInt }) => {
  const [filingData, setFilingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFilings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/filings/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true });
      setFilingData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch filing status');
    } finally {
      setLoading(false);
    }
  }, [clientId, yearInt, monthInt]);

  useEffect(() => { fetchFilings(); }, [fetchFilings]);

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4" data-testid="filing-status-tab">
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
        <p className="text-sm text-slate-600">
          Upload the filed return PDF along with the filing date and acknowledgement number for each return.
          <span className="font-medium"> GSTR-1 is due by the 11th</span> and <span className="font-medium">GSTR-3B by the 20th</span> of the following month.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <FilingReturnCard returnType="gstr1" label="GSTR-1" dueDay={11} filingData={filingData?.gstr1} clientId={clientId} yearInt={yearInt} monthInt={monthInt} onUploaded={fetchFilings} />
        <FilingReturnCard returnType="gstr3b" label="GSTR-3B" dueDay={20} filingData={filingData?.gstr3b} clientId={clientId} yearInt={yearInt} monthInt={monthInt} onUploaded={fetchFilings} />
      </div>
    </div>
  );
};



const EditCategoryDialog = ({ editingTxn, setEditingTxn, newCategory, setNewCategory, onSave }) => (
  <Dialog open={!!editingTxn} onOpenChange={(open) => !open && setEditingTxn(null)}>
    <DialogContent className="sm:max-w-md" aria-describedby="category-form-desc">
      <DialogHeader><DialogTitle>Change Category</DialogTitle></DialogHeader>
      <p id="category-form-desc" className="sr-only">Change the transaction category</p>
      {editingTxn && (
        <div className="space-y-4 mt-4">
          <div className="p-3 bg-slate-50 rounded-sm text-sm">
            <p><strong>Invoice:</strong> {editingTxn.voucher_number}</p>
            <p><strong>Party:</strong> {editingTxn.party_name}</p>
          </div>
          <div>
            <Label>New Category</Label>
            <Select value={newCategory} onValueChange={setNewCategory}>
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setEditingTxn(null)} className="rounded-sm" data-testid="cancel-category-btn">Cancel</Button>
            <Button onClick={onSave} className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-sm" data-testid="save-category-btn">Save</Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const ExcludeReasonDialog = ({ excludeTxn, setExcludeTxn, excludeReason, setExcludeReason, onConfirm }) => (
  <Dialog open={!!excludeTxn} onOpenChange={(open) => !open && setExcludeTxn(null)}>
    <DialogContent className="sm:max-w-md" aria-describedby="exclude-reason-desc">
      <DialogHeader><DialogTitle>Exclude CN/DN</DialogTitle></DialogHeader>
      <p id="exclude-reason-desc" className="sr-only">Provide a reason for excluding this transaction</p>
      {excludeTxn && (
        <div className="space-y-4 mt-4">
          <div className="p-3 bg-slate-50 rounded-sm text-sm">
            <p><strong>Type:</strong> {excludeTxn.voucher_type}</p>
            <p><strong>Number:</strong> {excludeTxn.voucher_number}</p>
            <p><strong>Party:</strong> {excludeTxn.party_name}</p>
            <p><strong>Value:</strong> {formatCurrency(excludeTxn.invoice_value)}</p>
          </div>
          <div>
            <Label>Reason for Exclusion <span className="text-red-500">*</span></Label>
            <textarea
              value={excludeReason}
              onChange={(e) => setExcludeReason(e.target.value)}
              placeholder="e.g. Non-income debit note, Duplicate entry, Internal adjustment..."
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={3}
              data-testid="exclude-reason-input"
            />
            {excludeReason.trim() === '' && <p className="text-xs text-red-500 mt-1">Reason is mandatory</p>}
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setExcludeTxn(null)} className="rounded-sm" data-testid="cancel-exclude-btn">Cancel</Button>
            <Button onClick={onConfirm} disabled={!excludeReason.trim()} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm disabled:opacity-50" data-testid="confirm-exclude-btn">
              Exclude
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

// ─── Main Component ───

const ClientFiling = () => {
  const {
    clientId, monthInt, yearInt, years,
    client, activeTab, setActiveTab, gstr1Tab, setGstr1Tab,
    loading, uploading, uploadStatus,
    transactions, gstr1Summary, b2bData, b2cData, hsnSummary, reconciliation, gstr3bSummary, salesRegister,
    inputTransactions, inputSummary,
    gstr2bEntries, itcRecon, rcmWorking, gstr3bFull, diffTracker, anomalies, itcRegister,
    editingTxn, setEditingTxn, newCategory, setNewCategory,
    excludeTxn, setExcludeTxn, excludeReason, setExcludeReason,
    totals, b2bTransactions, cdnTransactions, reconStats,
    navigate, fetchData, handlePeriodChange, handleDeleteAllData, handleDeleteGstr1, handleDeleteGstr3b,
    handleUpload, handleExport, handleToggleExclude, handleConfirmExclude, handleBulkExclude, handleCategoryUpdate, onEditTxn,
  } = useClientFiling();

  const handleUpdateInputType = useCallback(async (transactionId, newType) => {
    try {
      await axios.put(`${BACKEND_URL}/api/input-transactions/${transactionId}/type`, { input_type: newType }, { withCredentials: true });
      fetchData();
    } catch (_err) {
      if (process.env.NODE_ENV !== 'production') console.error('Input type update failed:', _err.response?.data?.detail || _err.message);
    }
  }, [fetchData]);

  const handleUpdateGstin = useCallback(async (transactionId, newGstin) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/input-transactions/${transactionId}/gstin`, { gstin: newGstin }, { withCredentials: true });
      fetchData();
      return res.data;
    } catch (err) {
      return { error: err.response?.data?.detail || 'GSTIN update failed' };
    }
  }, [fetchData]);

  const [reconRunning, setReconRunning] = useState(false);
  const [reconStatus, setReconStatus] = useState(null);

  const handleRunReconciliation = useCallback(async (type = 'normal') => {
    setReconRunning(true);
    setReconStatus(null);
    try {
      const endpoint = type === 'rcm'
        ? `${BACKEND_URL}/api/rcm-reconciliation/${clientId}/${yearInt}/${monthInt}`
        : `${BACKEND_URL}/api/itc-reconciliation/${clientId}/${yearInt}/${monthInt}`;
      const res = await axios.post(endpoint, {}, { withCredentials: true });
      setReconStatus({ success: true, message: res.data.message });
      fetchData();
    } catch (err) {
      setReconStatus({ success: false, message: err.response?.data?.detail || 'Reconciliation failed' });
    } finally {
      setReconRunning(false);
    }
  }, [clientId, yearInt, monthInt, fetchData]);

  const handleOverrideRecon = useCallback(async (reconId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/itc-reconciliation/${reconId}/override`, { status: newStatus }, { withCredentials: true });
      fetchData();
    } catch (_err) {
      if (process.env.NODE_ENV !== 'production') console.error('Reconciliation override failed:', _err.response?.data?.detail || _err.message);
    }
  }, [fetchData]);

  const [cfRunning, setCfRunning] = useState(false);
  const [cfStatus, setCfStatus] = useState(null);

  const handleCarryForward = useCallback(async () => {
    setCfRunning(true);
    setCfStatus(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/carry-forward/${clientId}/${yearInt}/${monthInt}`, {}, { withCredentials: true });
      setCfStatus({ success: true, message: res.data.message });
      fetchData();
    } catch (err) {
      setCfStatus({ success: false, message: err.response?.data?.detail || 'Carry forward failed' });
    } finally {
      setCfRunning(false);
    }
  }, [clientId, yearInt, monthInt, fetchData]);

  const handleResolveDiff = useCallback(async (diffId, remarks) => {
    try {
      await axios.put(`${BACKEND_URL}/api/difference-tracker/${diffId}/resolve`, { remarks, period: `${yearInt}-${String(monthInt).padStart(2, '0')}` }, { withCredentials: true });
      fetchData();
    } catch (_err) {
      if (process.env.NODE_ENV !== 'production') console.error('Difference resolve failed:', _err.response?.data?.detail || _err.message);
    }
  }, [yearInt, monthInt, fetchData]);

  if (!client) return <Layout><LoadingState /></Layout>;

  return (
    <Layout>
      <TooltipProvider delayDuration={200}>
      <div className="space-y-6" data-testid="client-filing-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-sm p-2" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{client.name}</h1>
              <p className="text-sm text-slate-500 font-mono">{client.gstin}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={monthInt.toString()} onValueChange={(v) => handlePeriodChange(yearInt, parseInt(v))}>
              <SelectTrigger className="w-32 rounded-sm" data-testid="filing-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={yearInt.toString()} onValueChange={(v) => handlePeriodChange(parseInt(v), monthInt)}>
              <SelectTrigger className="w-24 rounded-sm" data-testid="filing-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={fetchData} variant="outline" className="rounded-sm" data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid="delete-data-btn">
                  <Trash2 className="w-4 h-4 mr-1.5" />Clear Data
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={handleDeleteGstr1} className="text-red-600 focus:text-red-700" data-testid="delete-gstr1-btn">
                  Clear GSTR-1 Data
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDeleteGstr3b} className="text-red-600 focus:text-red-700" data-testid="delete-gstr3b-btn">
                  Clear GSTR-3B Data
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleDeleteAllData} className="text-red-600 font-semibold focus:text-red-700" data-testid="delete-all-btn">
                  Clear ALL Data
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100 rounded-sm p-1">
            <TabsTrigger value="gstr1" className="rounded-sm px-6" data-testid="tab-gstr1">GSTR-1</TabsTrigger>
            <TabsTrigger value="gstr3b" className="rounded-sm px-6" data-testid="tab-gstr3b">GSTR-3B</TabsTrigger>
            <TabsTrigger value="anomalies" className="rounded-sm px-6" data-testid="tab-anomalies">
              Anomalies
              {anomalies?.total > 0 && <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-red-500 text-white rounded-full">{anomalies.total}</span>}
            </TabsTrigger>
            <TabsTrigger value="filing" className="rounded-sm px-6" data-testid="tab-filing">Filing Status</TabsTrigger>
          </TabsList>

          <TabsContent value="gstr1" className="space-y-4 mt-4">
            {/* Upload Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <UploadSection title="Voucher Master (XML)" description="Upload Voucher Master to map custom voucher types (per-client)" accept=".xml" onUpload={(ref) => handleUpload('vouchermaster', 'voucher-master', ref)} uploading={uploading.vouchermaster} status={uploadStatus.vouchermaster} />
              <UploadSection title="Sales Data (Tally XML)" description="Upload one or more Tally Daybook XMLs with sales, debit notes, credit notes" accept=".xml" multiple onUpload={(ref) => handleUpload('sales', 'sales', ref)} uploading={uploading.sales} status={uploadStatus.sales} />
              {client.e_invoice_applicable && (
                <UploadSection title="E-Invoice Report (Excel)" description="Upload one or more E-Invoice portal reports for reconciliation" accept=".xlsx,.xls" multiple onUpload={(ref) => handleUpload('einvoice', 'einvoice', ref)} uploading={uploading.einvoice} status={uploadStatus.einvoice} />
              )}
              <UploadSection title="Rate Master - Stock Items (Tally XML)" description="Upload Stock Item HSN rate setup XML" accept=".xml" onUpload={(ref) => handleUpload('ratemaster', 'rate-master', ref)} uploading={uploading.ratemaster} status={uploadStatus.ratemaster} />
              <UploadSection title="Rate Master - Ledgers (Tally XML)" description="Upload Ledger-wise HSN/SAC rate setup XML" accept=".xml" onUpload={(ref) => handleUpload('ledgerratemaster', 'ledger-rate-master', ref)} uploading={uploading.ledgerratemaster} status={uploadStatus.ledgerratemaster} />
              <UploadSection title="Chart of Accounts (Tally XML)" description="Upload COA XML to filter non-income debit/credit notes" accept=".xml" onUpload={(ref) => handleUpload('coa', 'coa', ref)} uploading={uploading.coa} status={uploadStatus.coa} />
            </div>

            {/* GSTR-1 Sub-tabs */}
            <Tabs value={gstr1Tab} onValueChange={setGstr1Tab}>
              <TabsList className="bg-white border border-slate-200 rounded-sm p-1">
                <TabsTrigger value="summary" className="rounded-sm" data-testid="subtab-summary">Summary</TabsTrigger>
                <TabsTrigger value="all" className="rounded-sm" data-testid="subtab-all">All Transactions</TabsTrigger>
                <TabsTrigger value="b2b" className="rounded-sm" data-testid="subtab-b2b">B2B Invoices</TabsTrigger>
                <TabsTrigger value="b2c" className="rounded-sm" data-testid="subtab-b2c">B2C State-wise</TabsTrigger>
                <TabsTrigger value="hsn" className="rounded-sm" data-testid="subtab-hsn">HSN Summary</TabsTrigger>
                <TabsTrigger value="cdn" className="rounded-sm" data-testid="subtab-cdn">CN/DN</TabsTrigger>
                {client.e_invoice_applicable && <TabsTrigger value="recon" className="rounded-sm" data-testid="subtab-recon">E-Inv Recon</TabsTrigger>}
              </TabsList>

              <TabsContent value="summary" className="mt-4">
                <SummaryTab loading={loading} gstr1Summary={gstr1Summary} totals={totals} transactionsCount={transactions.length} salesRegister={salesRegister} onExport={handleExport} />
              </TabsContent>
              <TabsContent value="all" className="mt-4">
                <AllTransactionsTab loading={loading} transactions={transactions} onEdit={onEditTxn} />
              </TabsContent>
              <TabsContent value="b2b" className="mt-4">
                <B2BTab loading={loading} b2bData={b2bData} onEdit={onEditTxn} />
              </TabsContent>
              <TabsContent value="b2c" className="mt-4">
                <B2CStatewiseTab loading={loading} b2cData={b2cData} onExport={handleExport} />
              </TabsContent>
              <TabsContent value="hsn" className="mt-4">
                <HSNTab loading={loading} hsnSummary={hsnSummary} onExport={handleExport} />
              </TabsContent>
              <TabsContent value="cdn" className="mt-4">
                <CDNTab loading={loading} cdnTransactions={cdnTransactions} onToggleExclude={handleToggleExclude} onBulkExclude={handleBulkExclude} onExport={handleExport} />
              </TabsContent>
              {client.e_invoice_applicable && (
                <TabsContent value="recon" className="mt-4">
                  <ReconTab loading={loading} reconciliation={reconciliation} reconStats={reconStats} onExport={handleExport} />
                </TabsContent>
              )}
            </Tabs>
          </TabsContent>

          <TabsContent value="gstr3b" className="space-y-4 mt-4">
            <GSTR3BContent
              loading={loading} gstr3bSummary={gstr3bSummary} inputTransactions={inputTransactions}
              inputSummary={inputSummary} onUpdateInputType={handleUpdateInputType} onUpdateGstin={handleUpdateGstin}
              onUpload={handleUpload} uploading={uploading} uploadStatus={uploadStatus}
              gstr2bEntries={gstr2bEntries} itcRecon={itcRecon} rcmWorking={rcmWorking} gstr3bFull={gstr3bFull}
              onRunRecon={handleRunReconciliation} reconRunning={reconRunning} reconStatus={reconStatus}
              onOverrideRecon={handleOverrideRecon} fetchData={fetchData}
              diffTracker={diffTracker} onCarryForward={handleCarryForward} cfRunning={cfRunning} cfStatus={cfStatus}
              onResolveDiff={handleResolveDiff} onExport={handleExport} itcRegister={itcRegister}
            />
          </TabsContent>

          <TabsContent value="filing" className="space-y-4 mt-4">
            <FilingStatusTab clientId={clientId} yearInt={yearInt} monthInt={monthInt} />
          </TabsContent>

          <TabsContent value="anomalies" className="space-y-4 mt-4">
            <AnomaliesTab anomalies={anomalies} loading={loading} onExport={handleExport} />
          </TabsContent>
        </Tabs>

        {/* Edit Category Dialog */}
        <EditCategoryDialog
          editingTxn={editingTxn} setEditingTxn={setEditingTxn}
          newCategory={newCategory} setNewCategory={setNewCategory}
          onSave={handleCategoryUpdate}
        />

        {/* Exclude Reason Dialog */}
        <ExcludeReasonDialog
          excludeTxn={excludeTxn} setExcludeTxn={setExcludeTxn}
          excludeReason={excludeReason} setExcludeReason={setExcludeReason}
          onConfirm={handleConfirmExclude}
        />
      </div>
      </TooltipProvider>
    </Layout>
  );
};

export default ClientFiling;
